import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Upload, User, FileText, CreditCard, File, Search, Download, Camera, Scan, Loader, Loader2, CheckCircle2, X, History, Clock, MoreVertical, Users, UserCheck, UserX, Eye, Bike, AlertTriangle, Wrench, DollarSign, ChevronRight, Star } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import BulkUpload from '../components/BulkUpload';
import { downloadRiderTemplate } from '../utils/bulkUploadUtils';
import FileUpload from '../components/FileUpload';
import { useTranslation } from 'react-i18next';
import Tesseract from 'tesseract.js';
import { formatDateDisplay, formatDateForInput, truncateName, truncateCompanyName } from '../utils/dateUtils';
import DocumentUploadWizard from '../components/DocumentUploadWizard';
import { usePermissions } from '../utils/permissionUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import Pagination from '../components/Pagination';
import { formatErrorMessage } from '../utils/formatError';

const Riders = ({ user }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permission checks - CRITICAL for security
  const permissions = usePermissions(user, 'riders');
  const canCreate = permissions.canCreate;
  const canEdit = permissions.canEdit;
  const canDelete = permissions.canDelete;
  const canViewDocuments = permissions.canViewSubPage('documents');
  const canCreateDocuments = permissions.canCreateSubPage('documents');
  const canDeleteDocuments = permissions.canDeleteSubPage('documents');
  
  const [lesseeSummary, setLesseeSummary] = useState({ external: 0, inhouse: 0, inhouse_by_platform: {}, inhouse_other: 0 });
  const [showInhousePlatforms, setShowInhousePlatforms] = useState(false);
  const [riders, setRiders] = useState([]);
  const [filteredRiders, setFilteredRiders] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Server-side search
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    nationality: 'all',
    emiratesId: 'all',
    bikeNo: 'all',
    platform: 'all'
  });
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive', 'pending' - controlled by summary cards
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [motorcycles, setMotorcycles] = useState([]);
  const [pendingRiders, setPendingRiders] = useState([]); // Riders pending approval
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 50;
  
  // Stats for summary cards (fetched separately, not affected by pagination)
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0
  });
  
  // Rider history state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [riderHistory, setRiderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRider, setHistoryRider] = useState(null);
  
  // Platform history state
  const [platformHistoryDialogOpen, setPlatformHistoryDialogOpen] = useState(false);
  const [platformHistory, setPlatformHistory] = useState([]);
  const [platformHistoryLoading, setPlatformHistoryLoading] = useState(false);
  const [addPlatformHistoryDialogOpen, setAddPlatformHistoryDialogOpen] = useState(false);
  const [editPlatformHistoryIndex, setEditPlatformHistoryIndex] = useState(null); // For editing existing entry
  const [newPlatformEntry, setNewPlatformEntry] = useState({
    joining_date: '',
    last_date: '',
    platform: '',
    platform_id: '',
    custom_platform: '',
    new_mobile_number: '',
    sim_type: '',
    sim_owner: ''
  });
  
  // Vehicle, Fines, and Maintenance history states
  const [riderDetailTab, setRiderDetailTab] = useState('info'); // info, vehicle, fines, maintenance
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState(false);
  const [finesHistory, setFinesHistory] = useState({ fines: [], total_amount: 0, paid_amount: 0, unpaid_amount: 0 });
  const [finesHistoryLoading, setFinesHistoryLoading] = useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState({ maintenance: [], total_cost: 0 });
  const [maintenanceHistoryLoading, setMaintenanceHistoryLoading] = useState(false);
  
  // Helper function to format date as DD-MM-YYYY
  const formatDateDMY = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };
  
  // Document viewer state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documentViewerRider, setDocumentViewerRider] = useState(null);
  
  // Debug logging for state changes
  useEffect(() => {
    console.log('Lessees state updated:', lessees.length, 'items');
  }, [lessees]);
  
  useEffect(() => {
    console.log('Motorcycles state updated:', motorcycles.length, 'items');
  }, [motorcycles]);
  
  const [formData, setFormData] = useState({ 
    name: '', 
    date_of_birth: '', 
    email: '', 
    mobile_uae: '', 
    mobile_home_country: '',
    nationality: '',
    emirates_id: '',
    emirates_id_issue_date: '',
    emirates_id_expiry: '',
    employer: '',
    passport_number: '',
    passport_issue_date: '',
    passport_expiry: '',
    license_number: '',
    license_issue_date: '',
    license_expiry: '',
    visa_number: '',
    visa_expiry_date: '',
    labour_card_no: '',
    labour_card_expiry_date: '',
    person_code_no: '',
    uid_no: '',
    file_no: '',
    file_issue_date: '',
    file_expiry_date: '',
    platform: '',
    platform_id: '',  // Platform-specific rider ID
    custom_platform: '',
    status: 'active',
    lessee_id: '',
    lessee_name: '',
    assigned_bike_id: '',
    assigned_bike_plate: '',
    photo_url: '',
    emirates_id_doc_url: '',
    passport_doc_url: '',
    license_doc_url: '',
    designation: 'Motorcyclist',
    edenred_id: '',
    ohc_number: '',
    ohc_expiry_date: '',
    food_permit_number: '',
    food_permit_expiry_date: ''
  });

  // Multi-step wizard state
  const [currentStep, setCurrentStep] = useState(1); // 1: Emirates ID, 2: Passport, 3: License, 4: Summary
  const [useSequentialWizard, setUseSequentialWizard] = useState(false); // Toggle for new wizard
  const [showRiderForm, setShowRiderForm] = useState(false); // Show final form after wizard
  const [activeFormTab, setActiveFormTab] = useState('personal'); // Track active tab in final form
  
  // OCR-related state
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResults, setOcrResults] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  const [scannedDocumentType, setScannedDocumentType] = useState(null); // 'license', 'emirates_id', or 'passport'
  const [uploadedDocuments, setUploadedDocuments] = useState({
    license: null,
    emirates_id: null,
    passport: null
  });
  const [documentNames, setDocumentNames] = useState({
    license: {},
    emirates_id: {},
    passport: {}
  });
  const [individualAutoFilled, setIndividualAutoFilled] = useState({
    name: false,
    date_of_birth: false,
    nationality: false,
    emirates_id: false,
    emirates_id_issue_date: false,
    emirates_id_expiry: false,
    employer: false,
    passport_number: false,
    passport_issue_date: false,
    passport_expiry: false,
    license_number: false,
    license_issue_date: false,
    license_expiry: false
  });
  
  // State for bike assignment validation
  const [reassignmentDialog, setReassignmentDialog] = useState(false);
  const [reassignmentData, setReassignmentData] = useState(null);
  const [warningDialog, setWarningDialog] = useState(false);
  const [warningData, setWarningData] = useState(null);
  const [overruleNotes, setOverruleNotes] = useState('');
  
  // Bulk document upload state
  const [bulkDocDialogOpen, setBulkDocDialogOpen] = useState(false);
  const [bulkDocFile, setBulkDocFile] = useState(null);
  const [bulkDocUploading, setBulkDocUploading] = useState(false);
  const [bulkDocResults, setBulkDocResults] = useState(null);

  // Validate document before OCR
  const validateDocument = (file) => {
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error('❌ Invalid file format!\n\nPlease upload:\n• JPG or PNG images only\n• No PDFs or other formats');
      return false;
    }
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('❌ File too large!\n\nMaximum size: 10MB\nYour file: ' + (file.size / 1024 / 1024).toFixed(2) + 'MB\n\nPlease compress or take a new photo.');
      return false;
    }
    
    // Check minimum size (prevent tiny images)
    const minSize = 50 * 1024; // 50KB
    if (file.size < minSize) {
      toast.error('❌ File too small!\n\nMinimum size: 50KB\nPlease upload a higher quality image.');
      return false;
    }
    
    return true;
  };

  // OCR Processing function for rider documents using Tesseract.js
  const processRiderDocumentOCR = async (imageUrl, documentType, file) => {
    // Validate document first
    if (file && !validateDocument(file)) {
      return; // Stop if validation fails
    }
    
    setOcrProcessing(true);
    setScannedDocumentType(documentType);
    
    let processingToast;
    try {
      // Show progress toast
      processingToast = toast.loading('Scanning document... This may take 10-30 seconds');
      
      // Use the file directly instead of imageUrl for better compatibility
      const imageSource = file || imageUrl;
      
      // Perform real OCR using Tesseract.js
      // Configure Tesseract with English and Arabic language support and auto-rotation
      const { data: { text } } = await Tesseract.recognize(
        imageSource,
        'eng+ara', // English and Arabic language support
        {
          logger: (m) => {
            // Log progress for debugging
            if (m.status === 'recognizing text') {
              console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
            }
          },
          rotate: 'auto', // Auto-rotate images for better OCR
          tessedit_pageseg_mode: Tesseract.PSM.AUTO_OSD // Auto page segmentation with orientation detection
        }
      );
      
      // Dismiss loading toast
      toast.dismiss(processingToast);
      
      console.log('=== OCR DEBUG INFO ===');
      console.log('Document Type:', documentType);
      console.log('OCR Text Length:', text.length);
      console.log('=== FULL OCR TEXT ===');
      console.log(text);
      console.log('=== END OCR TEXT ===');
      
      // Parse the extracted text based on document type
      let extractedData = {};
      
      if (documentType === 'license') {
        extractedData = parseDriverLicenseText(text);
      } else if (documentType === 'emirates_id') {
        extractedData = parseEmiratesIDText(text);
      } else if (documentType === 'passport') {
        extractedData = parsePassportText(text);
      }
      
      console.log('Extracted Data:', extractedData);
      setOcrResults(extractedData);
      
      // Upload file to server after OCR processing
      let serverFileUrl = '';
      if (file) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          uploadFormData.append('entity_type', 'rider');
          
          // Map document type correctly
          let docType = documentType.replace('_doc_url', '').replace('_doc', '').replace('_url', '');
          // Convert photo_url to photo, emirates_id_doc_url to emirates_id, etc.
          if (docType === 'photo') docType = 'photo';
          else if (docType === 'emirates_id') docType = 'emirates_id';
          else if (docType === 'passport') docType = 'passport';
          else if (docType === 'license') docType = 'license';
          
          uploadFormData.append('document_type', docType);
          
          // If editing existing rider, pass rider ID for proper folder structure
          if (editingId) {
            uploadFormData.append('entity_id', editingId);
          }
          // For new riders, entity_id is not passed - will create date-based folder
          
          const uploadResponse = await axios.post(
            `${API}/upload`,
            uploadFormData,
            {
              headers: {
                ...getAuthHeader(),
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          serverFileUrl = uploadResponse.data.url;
          console.log(`File uploaded successfully to: ${uploadResponse.data.folder_path || 'OneDrive'}`);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.error('File upload failed. Please try again.');
          // Continue with OCR data even if upload fails
        }
      }
      
      // Auto-fill form with extracted data
      if (extractedData) {
        const newIndividualAutoFilled = {};
        
        setFormData(prevData => {
          const updatedFormData = { ...prevData };
          
          if (extractedData.name) {
            updatedFormData.name = extractedData.name;
            newIndividualAutoFilled.name = true;
          }
          if (extractedData.date_of_birth) {
            updatedFormData.date_of_birth = extractedData.date_of_birth;
            newIndividualAutoFilled.date_of_birth = true;
          }
          if (extractedData.nationality) {
            updatedFormData.nationality = extractedData.nationality;
            newIndividualAutoFilled.nationality = true;
          }
          if (extractedData.emirates_id) {
            updatedFormData.emirates_id = extractedData.emirates_id;
            newIndividualAutoFilled.emirates_id = true;
          }
          if (extractedData.emirates_id_issue_date) {
            updatedFormData.emirates_id_issue_date = extractedData.emirates_id_issue_date;
            newIndividualAutoFilled.emirates_id_issue_date = true;
          }
          if (extractedData.emirates_id_expiry) {
            updatedFormData.emirates_id_expiry = extractedData.emirates_id_expiry;
            newIndividualAutoFilled.emirates_id_expiry = true;
          }
          if (extractedData.employer) {
            updatedFormData.employer = extractedData.employer;
            newIndividualAutoFilled.employer = true;
          }
          if (extractedData.passport_number) {
            updatedFormData.passport_number = extractedData.passport_number;
            newIndividualAutoFilled.passport_number = true;
          }
          if (extractedData.passport_issue_date) {
            updatedFormData.passport_issue_date = extractedData.passport_issue_date;
            newIndividualAutoFilled.passport_issue_date = true;
          }
          if (extractedData.passport_expiry) {
            updatedFormData.passport_expiry = extractedData.passport_expiry;
            newIndividualAutoFilled.passport_expiry = true;
          }
          if (extractedData.license_number) {
            updatedFormData.license_number = extractedData.license_number;
            newIndividualAutoFilled.license_number = true;
          }
          if (extractedData.license_issue_date) {
            updatedFormData.license_issue_date = extractedData.license_issue_date;
            newIndividualAutoFilled.license_issue_date = true;
          }
          if (extractedData.license_expiry) {
            updatedFormData.license_expiry = extractedData.license_expiry;
            newIndividualAutoFilled.license_expiry = true;
          }
          
          // Store server document URL based on type (use server URL if available, fallback to temp URL)
          const urlToStore = serverFileUrl || imageUrl;
          if (documentType === 'license') {
            updatedFormData.license_doc_url = urlToStore;
          } else if (documentType === 'emirates_id') {
            updatedFormData.emirates_id_doc_url = urlToStore;
          } else if (documentType === 'passport') {
            updatedFormData.passport_doc_url = urlToStore;
          }
          
          return updatedFormData;
        });
        
        setIndividualAutoFilled(prev => ({...prev, ...newIndividualAutoFilled}));
        setAutoFilled(true);
        
        // Store document name for mismatch validation
        setDocumentNames(prev => ({
          ...prev,
          [documentType]: extractedData
        }));
        
        // Comprehensive Document Validation
        const existingDocs = Object.entries(documentNames)
          .filter(([type, data]) => type !== documentType && (data.name || data.date_of_birth))
          .map(([type, data]) => ({ type, name: data.name, dob: data.date_of_birth }));
        
        const validationErrors = [];
        
        // 1. Name Mismatch Check
        if (existingDocs.length > 0 && extractedData.name) {
          const currentName = extractedData.name.toLowerCase().trim();
          const nameMismatch = existingDocs.find(doc => {
            if (!doc.name) return false;
            const docName = doc.name.toLowerCase().trim();
            // Check if names are significantly different (not just minor variations)
            const firstName1 = currentName.split(' ')[0];
            const firstName2 = docName.split(' ')[0];
            return !currentName.includes(firstName2) && !docName.includes(firstName1);
          });
          
          if (nameMismatch) {
            const docType = nameMismatch.type === 'license' ? 'Driving License' : 
                           nameMismatch.type === 'emirates_id' ? 'Emirates ID' : 'Passport';
            validationErrors.push(`Name mismatch: Current "${extractedData.name}" vs ${docType} "${nameMismatch.name}"`);
          }
        }
        
        // 2. Date of Birth Mismatch Check
        if (existingDocs.length > 0 && extractedData.date_of_birth) {
          const currentDOB = extractedData.date_of_birth;
          const dobMismatch = existingDocs.find(doc => {
            if (!doc.dob) return false;
            return doc.dob !== currentDOB;
          });
          
          if (dobMismatch) {
            const docType = dobMismatch.type === 'license' ? 'Driving License' : 
                           dobMismatch.type === 'emirates_id' ? 'Emirates ID' : 'Passport';
            validationErrors.push(`Date of Birth mismatch: Current "${currentDOB}" vs ${docType} "${dobMismatch.dob}"`);
          }
        }
        
        // 3. Show validation errors
        if (validationErrors.length > 0) {
          const currentDocType = documentType === 'license' ? 'Driving License' : 
                                documentType === 'emirates_id' ? 'Emirates ID' : 'Passport';
          toast.error(
            `⚠️ Document Mismatch Detected!\n\n${currentDocType}:\n${validationErrors.join('\n')}\n\n⚠️ Please verify you're uploading documents for the SAME person!`,
            {
              duration: 10000,
              style: {
                background: '#FEE2E2',
                border: '2px solid #DC2626',
                padding: '16px',
                maxWidth: '500px'
              }
            }
          );
        }
        
        const docTypeName = documentType === 'license' ? 'Driving License' : 
                          documentType === 'emirates_id' ? 'Emirates ID' : 'Passport';
        toast.success(`${docTypeName} scanned successfully! Form fields have been auto-filled. Use individual clear buttons to edit specific fields.`);
      }
      
    } catch (error) {
      console.error('OCR processing failed:', error);
      // Dismiss loading toast if it exists
      if (processingToast) {
        toast.dismiss(processingToast);
      }
      
      // Don't upload - ask user to rescan with better quality
      toast.error(
        '❌ Unable to Scan Document!\n\n' +
        '📸 Please re-upload with:\n' +
        '✓ Clear, well-lit photo\n' +
        '✓ Document flat (not folded/bent)\n' +
        '✓ All text clearly visible\n' +
        '✓ Proper orientation (not upside down)\n' +
        '✓ Standard format (JPG, PNG)\n\n' +
        '⚠️ Document NOT uploaded. Please try again with a clearer image.',
        { duration: 10000 }
      );
      
      // Clear the failed upload from state
      setUploadedDocuments(prev => {
        const updated = { ...prev };
        delete updated[documentType];
        return updated;
      });
      
    } finally {
      setOcrProcessing(false);
    }
  };

  // Parse UAE Driving License text
  const parseDriverLicenseText = (text) => {
    const extracted = {};
    
    // License number patterns: various formats
    const licensePatterns = [
      /License\s*No[:\s]*([A-Z0-9-]+)/i,
      /Lic[:\s]*No[:\s]*([A-Z0-9-]+)/i,
      /Traffic\s*File[:\s]*([A-Z0-9-]+)/i,
      /(?:^|\n)(\d{9,})/m  // Fallback: 9+ digits standalone
    ];
    
    for (const pattern of licensePatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.license_number = match[1].trim();
        break;
      }
    }
    
    // Name patterns - look for "Name:" or standalone capitalized name
    const namePatterns = [
      /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)([A-Z][A-Z\s]{5,})/m  // Fallback: All caps name
    ];
    
    for (const pattern of namePatterns) {
      const match = text.match(pattern);
      if (match && !match[1].includes('EMIRATES') && !match[1].includes('LICENSE')) {
        extracted.name = match[1].trim();
        break;
      }
    }
    
    // Date of birth patterns
    const dobPatterns = [
      /(?:Date\s*of\s*Birth|D\.?O\.?B\.?|Birth)[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i,
      /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/  // Fallback: any date format
    ];
    
    for (const pattern of dobPatterns) {
      const match = text.match(pattern);
      if (match) {
        const dateStr = match[1];
        const [day, month, year] = dateStr.split(/[\/\-]/);
        extracted.date_of_birth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        break;
      }
    }
    
    // Issue date patterns
    const issueDatePatterns = [
      /Issue\s*Date[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i,
      /Issued[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i,
      /Date\s*of\s*Issue[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i
    ];
    
    for (const pattern of issueDatePatterns) {
      const match = text.match(pattern);
      if (match) {
        const dateStr = match[1];
        const [day, month, year] = dateStr.split(/[\/\-]/);
        extracted.license_issue_date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        break;
      }
    }
    
    // Expiry date patterns
    const expiryPatterns = [
      /Expiry\s*Date[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i,
      /Valid\s*Until[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i,
      /Expires[:\s]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4})/i
    ];
    
    for (const pattern of expiryPatterns) {
      const match = text.match(pattern);
      if (match) {
        const dateStr = match[1];
        const [day, month, year] = dateStr.split(/[\/\-]/);
        extracted.license_expiry = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        break;
      }
    }
    
    // Nationality extraction from license
    const nationalityPatterns = [
      /Nationality[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)(INDIAN|PAKISTANI|BANGLADESHI|FILIPINO|EGYPTIAN|JORDANIAN|SYRIAN|SRI\s*LANKAN|NEPALESE)/mi
    ];
    
    for (const pattern of nationalityPatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.nationality = match[1].trim();
        break;
      }
    }
    
    return extracted;
  };

  // Parse Emirates ID text
  const parseEmiratesIDText = (text) => {
    const extracted = {};
    
    console.log('Parsing Emirates ID, text length:', text.length);
    
    // Emirates ID Number format: 784-YYYY-NNNNNNN-N or without dashes
    // Try multiple patterns with more flexibility
    const idPatterns = [
      /(?:ID\s*Number|IDN|Identity\s*Number|Card\s*Number)[:\s\/]*([78]\d{2}[\s-]?\d{4}[\s-]?\d{7,8}[\s-]?\d{1})/i,
      /([78]\d{2}[\s-]\d{4}[\s-]\d{7,8}[\s-]\d{1})/,  // With separators
      /([78]\d{14,15})/,  // Just digits, 15-16 chars
      /(\d{3}-\d{4}-\d{7}-\d{1})/  // Exact format XXX-XXXX-XXXXXXX-X
    ];
    
    for (const pattern of idPatterns) {
      const match = text.match(pattern);
      if (match) {
        let idNumber = match[1].replace(/[^\d]/g, '');  // Remove all non-digits first
        console.log('Raw ID found:', idNumber);
        // Ensure proper format with dashes
        if (idNumber.length >= 15) {
          idNumber = `${idNumber.slice(0,3)}-${idNumber.slice(3,7)}-${idNumber.slice(7,14)}-${idNumber.slice(14,15)}`;
          extracted.emirates_id = idNumber;
          console.log('Formatted Emirates ID:', idNumber);
          break;
        }
      }
    }
    
    // Name extraction - look for capitalized names
    const namePatterns = [
      /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)([A-Z][A-Z\s]{8,})/m,  // All caps name, at least 8 chars
      /([A-Z][a-z]+\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/  // Multiple capitalized words
    ];
    
    for (const pattern of namePatterns) {
      const match = text.match(pattern);
      if (match && !match[1].includes('EMIRATES') && !match[1].includes('UNITED') && !match[1].includes('IDENTITY')) {
        extracted.name = match[1].trim();
        break;
      }
    }
    
    // Nationality extraction
    const nationalityPatterns = [
      /Nationality[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)(INDIAN|PAKISTANI|BANGLADESHI|FILIPINO|EGYPTIAN|JORDANIAN|SYRIAN|SRI\s*LANKAN|NEPALESE|UAE|EMIRATI)/mi
    ];
    
    for (const pattern of nationalityPatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.nationality = match[1].trim();
        break;
      }
    }
    
    // Date of Birth - multiple formats, more flexible
    const dobPatterns = [
      /(?:Date\s*of\s*Birth|DOB|Birth\s*Date|Born)[:\s]*(\d{1,2}[\/\-\.\s]\d{1,2}[\/\-\.\s]\d{4})/i,
      /Birth[:\s]*(\d{1,2}[\/\-\.\s]\d{1,2}[\/\-\.\s]\d{4})/i,
      /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.](19|20)\d{2})/  // Any date format with 19xx or 20xx
    ];
    
    for (const pattern of dobPatterns) {
      const match = text.match(pattern);
      if (match) {
        const dateStr = match[1];
        const parts = dateStr.split(/[\/\-\.\s]+/);
        if (parts.length >= 3) {
          const [day, month, year] = parts;
          if (year && year.length === 4 && parseInt(year) >= 1950 && parseInt(year) <= 2010) {
            extracted.date_of_birth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            console.log('DOB extracted:', extracted.date_of_birth);
            break;
          }
        }
      }
    }
    
    // Extract ALL dates from the text first - try multiple formats
    const allDates = [];
    
    // Pattern 1: DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    const dateRegex1 = /\b(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})\b/g;
    let match;
    
    while ((match = dateRegex1.exec(text)) !== null) {
      const day = match[1];
      const month = match[2];
      const year = match[3];
      
      if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
          parseInt(day) >= 1 && parseInt(day) <= 31 &&
          parseInt(year) >= 1950 && parseInt(year) <= 2040) {
        
        allDates.push({
          raw: match[0],
          day: day,
          month: month,
          year: year,
          formatted: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
          timestamp: new Date(year, month - 1, day).getTime()
        });
      }
    }
    
    // Pattern 2: DD/MM/YY (2-digit year)
    const dateRegex2 = /\b(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})\b/g;
    
    while ((match = dateRegex2.exec(text)) !== null) {
      const day = match[1];
      const month = match[2];
      let year = match[3];
      
      // Convert 2-digit year to 4-digit (19/20/21 -> 2019/2020/2021)
      const yearNum = parseInt(year);
      if (yearNum <= 40) {
        year = '20' + year.padStart(2, '0');
      } else {
        year = '19' + year.padStart(2, '0');
      }
      
      if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
          parseInt(day) >= 1 && parseInt(day) <= 31) {
        
        allDates.push({
          raw: match[0],
          day: day,
          month: month,
          year: year,
          formatted: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
          timestamp: new Date(year, month - 1, day).getTime()
        });
      }
    }
    
    // Pattern 3: YYYY/MM/DD or YYYY-MM-DD
    const dateRegex3 = /\b(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})\b/g;
    
    while ((match = dateRegex3.exec(text)) !== null) {
      const year = match[1];
      const month = match[2];
      const day = match[3];
      
      if (parseInt(month) >= 1 && parseInt(month) <= 12 && 
          parseInt(day) >= 1 && parseInt(day) <= 31 &&
          parseInt(year) >= 1950 && parseInt(year) <= 2040) {
        
        allDates.push({
          raw: match[0],
          day: day,
          month: month,
          year: year,
          formatted: `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
          timestamp: new Date(year, month - 1, day).getTime()
        });
      }
    }
    
    console.log('All dates found:', allDates.map(d => `${d.raw} -> ${d.formatted}`));
    
    // Sort dates by timestamp
    allDates.sort((a, b) => a.timestamp - b.timestamp);
    
    // Smart date assignment:
    // - DOB: Already extracted above
    // - Issue Date: Date between 2010-2024 (past dates)
    // - Expiry Date: Date between 2024-2035 (future dates)
    
    const now = new Date().getTime();
    const dobTimestamp = extracted.date_of_birth ? new Date(extracted.date_of_birth).getTime() : 0;
    
    // Filter out DOB if already extracted
    const remainingDates = allDates.filter(d => Math.abs(d.timestamp - dobTimestamp) > 86400000); // More than 1 day difference
    
    // Find Issue Date (past date, after 2010)
    const issueDateCandidate = remainingDates.find(d => {
      const year = parseInt(d.year);
      return year >= 2010 && year <= 2024 && d.timestamp < now;
    });
    
    if (issueDateCandidate) {
      extracted.emirates_id_issue_date = issueDateCandidate.formatted;
      console.log('Issue Date extracted:', extracted.emirates_id_issue_date);
    }
    
    // Find Expiry Date (future date, after 2024)
    const expiryDateCandidate = remainingDates.find(d => {
      const year = parseInt(d.year);
      return year >= 2024 && year <= 2035 && d.timestamp > now;
    });
    
    if (expiryDateCandidate) {
      extracted.emirates_id_expiry = expiryDateCandidate.formatted;
      console.log('Expiry Date extracted:', extracted.emirates_id_expiry);
    }
    
    // Fallback: If we have exactly 3 dates (DOB, Issue, Expiry)
    if (!extracted.emirates_id_issue_date && !extracted.emirates_id_expiry && allDates.length >= 3) {
      // Assuming order: oldest = DOB, middle = Issue, newest = Expiry
      const sortedDates = allDates.slice();
      if (sortedDates.length >= 2) {
        // Second date is likely issue date
        extracted.emirates_id_issue_date = sortedDates[sortedDates.length - 2].formatted;
        console.log('Issue Date (fallback):', extracted.emirates_id_issue_date);
      }
      if (sortedDates.length >= 1) {
        // Last date is likely expiry
        extracted.emirates_id_expiry = sortedDates[sortedDates.length - 1].formatted;
        console.log('Expiry Date (fallback):', extracted.emirates_id_expiry);
      }
    }
    
    // Employer/Sponsor extraction
    const employerPatterns = [
      /Employer[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i,
      /Sponsor[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i,
      /Company[:\s]+([A-Z][A-Za-z\s&.,-]+(?:LLC|LTD|INC|CO|COMPANY|CORPORATION)?)/i
    ];
    
    for (const pattern of employerPatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.employer = match[1].trim();
        break;
      }
    }
    
    return extracted;
  };

  // Parse Passport text
  const parsePassportText = (text) => {
    const extracted = {};
    
    // Passport number patterns
    const passportPatterns = [
      /Passport\s*No[:\s]*([A-Z0-9]+)/i,
      /Passport\s*Number[:\s]*([A-Z0-9]+)/i,
      /(?:^|\n)([A-Z]{1,2}\d{6,9})/m,  // Common format: 1-2 letters + 6-9 digits
      /No[:\s]*([A-Z0-9]{6,})/i
    ];
    
    for (const pattern of passportPatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.passport_number = match[1].trim();
        break;
      }
    }
    
    // Name extraction from passport (can be surname + given names or full name)
    const surnameMatch = text.match(/Surname[:\s]+([A-Z][A-Za-z\s]+)/i);
    const givenMatch = text.match(/Given\s*Names?[:\s]+([A-Z][A-Za-z\s]+)/i);
    
    if (surnameMatch && givenMatch) {
      extracted.name = `${givenMatch[1].trim()} ${surnameMatch[1].trim()}`;
    } else {
      // Try to find full name pattern
      const namePatterns = [
        /Name[:\s]+([A-Z][A-Za-z\s]+)/i,
        /(?:^|\n)([A-Z][A-Z\s]{8,})/m
      ];
      
      for (const pattern of namePatterns) {
        const match = text.match(pattern);
        if (match && !match[1].includes('PASSPORT') && !match[1].includes('REPUBLIC')) {
          extracted.name = match[1].trim();
          break;
        }
      }
    }
    
    // Date of Birth - handle both "DD MMM YYYY" and "DD/MM/YYYY" formats
    const months = { 
      JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
      JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
      JANUARY: '01', FEBRUARY: '02', MARCH: '03', APRIL: '04', JUNE: '06',
      JULY: '07', AUGUST: '08', SEPTEMBER: '09', OCTOBER: '10', NOVEMBER: '11', DECEMBER: '12'
    };
    
    const dobPatterns = [
      /(?:Date\s*of\s*Birth|DOB)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
      /(?:Date\s*of\s*Birth|DOB)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
      /Birth[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i
    ];
    
    for (const pattern of dobPatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match.length === 4) {
          // Format: DD MMM YYYY
          const day = match[1].padStart(2, '0');
          const monthStr = match[2].toUpperCase().slice(0, 3);
          const month = months[monthStr] || '01';
          const year = match[3];
          extracted.date_of_birth = `${year}-${month}-${day}`;
        } else {
          // Format: DD/MM/YYYY
          const [day, month, year] = match[1].split(/[\/\-\.]/);
          if (year && year.length === 4) {
            extracted.date_of_birth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        break;
      }
    }
    
    // Issue Date
    const issueDatePatterns = [
      /(?:Date\s*of\s*Issue|Issue\s*Date|Issued)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
      /(?:Date\s*of\s*Issue|Issue\s*Date|Issued)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i
    ];
    
    for (const pattern of issueDatePatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match.length === 4) {
          // Format: DD MMM YYYY
          const day = match[1].padStart(2, '0');
          const monthStr = match[2].toUpperCase().slice(0, 3);
          const month = months[monthStr] || '01';
          const year = match[3];
          extracted.passport_issue_date = `${year}-${month}-${day}`;
        } else {
          // Format: DD/MM/YYYY
          const [day, month, year] = match[1].split(/[\/\-\.]/);
          if (year && year.length === 4) {
            extracted.passport_issue_date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        break;
      }
    }
    
    // Expiry Date
    const expiryPatterns = [
      /(?:Date\s*of\s*Expiry|Expiry\s*Date|Expires)[:\s]*(\d{1,2})\s+([A-Z]{3,})\s+(\d{4})/i,
      /(?:Date\s*of\s*Expiry|Expiry\s*Date|Expires)[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i,
      /Valid\s*Until[:\s]*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/i
    ];
    
    for (const pattern of expiryPatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match.length === 4) {
          // Format: DD MMM YYYY
          const day = match[1].padStart(2, '0');
          const monthStr = match[2].toUpperCase().slice(0, 3);
          const month = months[monthStr] || '01';
          const year = match[3];
          extracted.passport_expiry = `${year}-${month}-${day}`;
        } else {
          // Format: DD/MM/YYYY
          const [day, month, year] = match[1].split(/[\/\-\.]/);
          if (year && year.length === 4) {
            extracted.passport_expiry = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        }
        break;
      }
    }
    
    // Nationality extraction from passport
    const nationalityPatterns = [
      /Nationality[:\s]+([A-Z][A-Za-z\s]+)/i,
      /(?:^|\n)(INDIAN|PAKISTANI|BANGLADESHI|FILIPINO|EGYPTIAN|JORDANIAN|SYRIAN|SRI\s*LANKAN|NEPALESE)/mi
    ];
    
    for (const pattern of nationalityPatterns) {
      const match = text.match(pattern);
      if (match) {
        extracted.nationality = match[1].trim();
        break;
      }
    }
    
    return extracted;
  };

  // Clear individual auto-filled field
  const clearIndividualField = (fieldName, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: defaultValue
    }));
    
    setIndividualAutoFilled(prev => ({
      ...prev,
      [fieldName]: false
    }));
    
    toast.info(`${fieldName.replace(/_/g, ' ')} cleared. You can now enter manually.`);
  };

  // Remove uploaded document
  const removeUploadedDocument = (documentType) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentType]: null
    }));
    
    setDocumentNames(prev => ({
      ...prev,
      [documentType]: {}
    }));
    
    // Clear the document URL from formData
    const urlFieldMap = {
      'license': 'license_doc_url',
      'emirates_id': 'emirates_id_doc_url',
      'passport': 'passport_doc_url'
    };
    
    setFormData(prev => ({
      ...prev,
      [urlFieldMap[documentType]]: ''
    }));
    
    const docTypeName = documentType === 'license' ? 'Driving License' : 
                       documentType === 'emirates_id' ? 'Emirates ID' : 'Passport';
    toast.success(`${docTypeName} removed successfully`);
  };

  useEffect(() => {
    console.log('Component mounted - fetching initial data');
    fetchData();
  }, []);
  
  // Ensure data is fresh when dialog opens
  useEffect(() => {
    if (dialogOpen && (lessees.length === 0 || motorcycles.length === 0)) {
      console.log('Dialog opened with missing data - refetching');
      fetchData();
    }
  }, [dialogOpen]);

  useEffect(() => {
    // Apply advanced filters and status filter when riders changes
    // NOTE: Search is handled server-side via fetchData(), not client-side
    let filtered = riders;

    // Apply advanced filters
    if (advancedFilters.nationality && advancedFilters.nationality !== 'all') {
      filtered = filtered.filter(rider => rider.nationality === advancedFilters.nationality);
    }
    if (advancedFilters.emiratesId && advancedFilters.emiratesId !== 'all') {
      filtered = filtered.filter(rider => rider.emirates_id === advancedFilters.emiratesId);
    }
    if (advancedFilters.platform && advancedFilters.platform !== 'all') {
      filtered = filtered.filter(rider => rider.platform === advancedFilters.platform);
    }
    
    // Status filter from summary cards is now handled server-side
    // Only handle pending filter client-side (uses separate pendingRiders list)
    if (statusFilter === 'pending') {
      filtered = pendingRiders;
    }

    setFilteredRiders(filtered);
  }, [riders, advancedFilters, statusFilter, pendingRiders]);

  const fetchData = async (page = currentPage, search = activeSearch, riderStatus = null) => {
    try {
      setLoading(true);
      if (search) setIsSearching(true);
      const skip = (page - 1) * ITEMS_PER_PAGE;
      
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', skip);
      if (search) params.append('search', search);
      if (riderStatus && riderStatus !== 'all' && riderStatus !== 'pending') {
        params.append('status', riderStatus);
      }
      
      // Build count params (same filters)
      const countParams = new URLSearchParams();
      if (search) countParams.append('search', search);
      if (riderStatus && riderStatus !== 'all' && riderStatus !== 'pending') {
        countParams.append('status', riderStatus);
      }
      
      const [ridersRes, countRes, statsRes, lesseeSummaryRes, lesseesRes, motorcyclesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/riders?${params.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/count?${countParams.toString()}`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/lessee-summary`, { headers: getAuthHeader() }).catch(() => ({ data: { external: 0, inhouse: 0, inhouse_by_platform: {}, inhouse_other: 0 } })),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/approvals/pending`, { headers: getAuthHeader() }).catch(() => ({ data: { riders: [] } }))
      ]);
      console.log('Fetched riders:', ridersRes.data.length);
      setRiders(ridersRes.data);
      setFilteredRiders(ridersRes.data); // Server already filtered
      setTotalCount(countRes.data.count || 0);
      setStats(statsRes.data);
      setLesseeSummary(lesseeSummaryRes.data);
      setLessees(lesseesRes.data);
      setMotorcycles(motorcyclesRes.data);
      setPendingRiders(pendingRes.data?.riders || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };
  
  // Trigger search manually (button click or Enter key)
  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setCurrentPage(1);
    fetchData(1, searchQuery);
  };

  // When search text is cleared (backspace/delete), auto-reset to original state
  useEffect(() => {
    if (searchQuery === '' && activeSearch !== '') {
      setActiveSearch('');
      setCurrentPage(1);
      fetchData(1, '');
    }
  }, [searchQuery]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setCurrentPage(1);
    fetchData(1, '');
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData(page, activeSearch, statusFilter);
  };

  const fetchRiders = async () => {
    try {
      const response = await axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() });
      setRiders(response.data);
    } catch (error) {
      toast.error('Failed to fetch riders');
    }
  };

  // Compute unique platform names from all rider data for filter dropdown
  const availablePlatforms = useMemo(() => {
    const platforms = new Set();
    (riders || []).forEach(rider => {
      if (rider.platform) platforms.add(rider.platform);
    });
    return [...platforms].sort();
  }, [riders]);

  // Fetch platform history for a rider
  const fetchPlatformHistory = async (riderId) => {
    setPlatformHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/riders/${riderId}/platform-history`, { headers: getAuthHeader() });
      setPlatformHistory(response.data.platform_history || []);
    } catch (error) {
      console.error('Failed to fetch platform history:', error);
      setPlatformHistory([]);
    } finally {
      setPlatformHistoryLoading(false);
    }
  };

  // Fetch vehicle assignment history for a rider
  const fetchVehicleHistory = async (riderId) => {
    setVehicleHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/riders/${riderId}/vehicle-history`, { headers: getAuthHeader() });
      setVehicleHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch vehicle history:', error);
      setVehicleHistory([]);
    } finally {
      setVehicleHistoryLoading(false);
    }
  };

  // Fetch fines history for a rider
  const fetchFinesHistory = async (riderId) => {
    setFinesHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/riders/${riderId}/fines-history`, { headers: getAuthHeader() });
      setFinesHistory({
        fines: response.data.fines || [],
        total_amount: response.data.total_amount || 0,
        paid_amount: response.data.paid_amount || 0,
        unpaid_amount: response.data.unpaid_amount || 0,
        assigned_vehicles: response.data.assigned_vehicles || []
      });
    } catch (error) {
      console.error('Failed to fetch fines history:', error);
      setFinesHistory({ fines: [], total_amount: 0, paid_amount: 0, unpaid_amount: 0, assigned_vehicles: [] });
    } finally {
      setFinesHistoryLoading(false);
    }
  };

  // Fetch maintenance history for a rider
  const fetchMaintenanceHistory = async (riderId) => {
    setMaintenanceHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/riders/${riderId}/maintenance-history`, { headers: getAuthHeader() });
      setMaintenanceHistory({
        maintenance: response.data.maintenance || [],
        total_cost: response.data.total_cost || 0,
        assigned_vehicles: response.data.assigned_vehicles || []
      });
    } catch (error) {
      console.error('Failed to fetch maintenance history:', error);
      setMaintenanceHistory({ maintenance: [], total_cost: 0, assigned_vehicles: [] });
    } finally {
      setMaintenanceHistoryLoading(false);
    }
  };

  // Add or edit platform history entry
  const handleAddPlatformHistory = async () => {
    if (!selectedRider || !newPlatformEntry.platform || !newPlatformEntry.joining_date) {
      toast.error('Please fill in platform and joining date');
      return;
    }
    // If SIM type is chosen, mobile number is required (and vice versa).
    if ((newPlatformEntry.sim_type && !newPlatformEntry.new_mobile_number)
        || (newPlatformEntry.new_mobile_number && !newPlatformEntry.sim_type)) {
      toast.error('Please fill in both SIM type and new mobile number');
      return;
    }

    try {
      let warnings = [];
      if (editPlatformHistoryIndex !== null) {
        // Editing existing entry - update the whole array
        const updatedHistory = [...platformHistory];
        updatedHistory[editPlatformHistoryIndex] = {
          ...updatedHistory[editPlatformHistoryIndex],
          ...newPlatformEntry
        };
        
        const res = await axios.put(`${API}/riders/${selectedRider.id}/platform-history`, {
          platform_history: updatedHistory
        }, { headers: getAuthHeader() });
        warnings = res.data?.warnings || [];

        toast.success('Platform history entry updated');
        setEditPlatformHistoryIndex(null);
      } else {
        // Adding new entry
        const res = await axios.post(`${API}/riders/${selectedRider.id}/platform-history/add`, newPlatformEntry, { headers: getAuthHeader() });
        warnings = res.data?.warnings || [];
        toast.success('Platform history entry added');
      }
      // Show SIM inventory mismatch warning (if any). Non-blocking toast.
      warnings.forEach((w) => {
        toast.warning(w, { duration: 8000 });
      });

      setAddPlatformHistoryDialogOpen(false);
      setNewPlatformEntry({ joining_date: '', last_date: '', platform: '', platform_id: '', custom_platform: '', new_mobile_number: '', sim_type: '', sim_owner: '' });
      fetchPlatformHistory(selectedRider.id);
      fetchRiders(); // Refresh rider data
    } catch (error) {
      toast.error('Failed to save platform history entry');
    }
  };

  // Open edit dialog for platform history entry
  const handleSetPrimaryPlatform = async (entryIndex) => {
    if (!selectedRider) return;
    const updatedHistory = platformHistory.map((entry, i) => ({
      ...entry,
      is_primary: i === entryIndex,
    }));
    try {
      await axios.put(`${API}/riders/${selectedRider.id}/platform-history`, {
        platform_history: updatedHistory,
      }, { headers: getAuthHeader() });
      const platName = updatedHistory[entryIndex].custom_platform || updatedHistory[entryIndex].platform || 'platform';
      toast.success(`${platName} marked as Primary Platform`);
      fetchPlatformHistory(selectedRider.id);
      fetchRiders();
    } catch (e) {
      toast.error('Failed to set primary platform');
    }
  };
  const handleEditPlatformHistory = (entry, index) => {
    setEditPlatformHistoryIndex(index);
    setNewPlatformEntry({
      joining_date: entry.joining_date || '',
      last_date: entry.last_date || '',
      platform: entry.platform || '',
      platform_id: entry.platform_id || '',
      custom_platform: entry.custom_platform || '',
      new_mobile_number: entry.new_mobile_number || '',
      sim_type: entry.sim_type || '',
      sim_owner: entry.sim_owner || ''
    });
    setAddPlatformHistoryDialogOpen(true);
  };

  const handleDownloadRiderDocuments = async (rider) => {
    try {
      toast.info('Preparing documents for download...');
      
      // Call backend endpoint to get zip file
      const response = await axios.get(
        `${API}/riders/${rider.id}/download-documents`,
        {
          headers: getAuthHeader(),
          responseType: 'blob' // Important for file download
        }
      );
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${rider.name.replace(/\s/g, '_')}_documents.zip`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      
      toast.success('Documents downloaded successfully!');
    } catch (error) {
      console.error('Failed to download documents:', error);
      if (error.response?.status === 404) {
        toast.error('No documents available for this rider');
      } else {
        toast.error('Failed to download documents. Please try again.');
      }
    }
  };

  // Handle view documents - fetch from categorized backend endpoint
  const [riderDocuments, setRiderDocuments] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const handleViewDocuments = async (rider) => {
    setDocumentViewerRider(rider);
    setDocumentViewerOpen(true);
    setLoadingDocs(true);
    setRiderDocuments(null);
    try {
      const res = await axios.get(`${API}/riders/${rider.id}/documents`, { headers: getAuthHeader() });
      setRiderDocuments(res.data);
    } catch (err) {
      console.error('Failed to fetch rider documents:', err);
      // Fallback to local data
      setRiderDocuments(null);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Download individual document
  const handleDownloadSingleDocument = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
      toast.success('Document downloaded!');
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };

  // Get list of available documents for a rider
  const getRiderDocuments = (rider) => {
    const documents = [];
    
    if (rider.photo_url) {
      documents.push({
        type: 'Photo',
        url: rider.photo_url,
        icon: 'photo'
      });
    }
    if (rider.emirates_id_front_url) {
      documents.push({
        type: 'Emirates ID (Front)',
        url: rider.emirates_id_front_url,
        icon: 'id'
      });
    }
    if (rider.emirates_id_back_url) {
      documents.push({
        type: 'Emirates ID (Back)',
        url: rider.emirates_id_back_url,
        icon: 'id'
      });
    }
    if (rider.emirates_id_doc_url && !rider.emirates_id_front_url) {
      documents.push({
        type: 'Emirates ID',
        url: rider.emirates_id_doc_url,
        icon: 'id'
      });
    }
    if (rider.passport_doc_url) {
      documents.push({
        type: 'Passport',
        url: rider.passport_doc_url,
        icon: 'passport'
      });
    }
    if (rider.license_doc_url) {
      documents.push({
        type: 'Driving License',
        url: rider.license_doc_url,
        icon: 'license'
      });
    }
    if (rider.employment_agreement_url) {
      documents.push({
        type: 'Employment Agreement',
        url: rider.employment_agreement_url,
        icon: 'document'
      });
    }
    if (rider.loan_agreement_url) {
      documents.push({
        type: 'Loan Agreement',
        url: rider.loan_agreement_url,
        icon: 'document'
      });
    }
    if (rider.receipt_cash_paid_url) {
      documents.push({
        type: 'Receipt (Cash Paid)',
        url: rider.receipt_cash_paid_url,
        icon: 'document'
      });
    }
    
    return documents;
  };

  const validateBikeAssignment = async () => {
    if (!formData.assigned_bike_id) {
      return true; // No bike assigned, no validation needed
    }

    try {
      const response = await axios.post(
        `${API}/riders/validate-bike-assignment`,
        {
          bike_id: formData.assigned_bike_id,
          rider_id: editingId
        },
        { headers: getAuthHeader() }
      );

      if (response.data.valid) {
        return true;
      }

      // Check for reassignment needed
      if (response.data.reassignment_needed) {
        setReassignmentData(response.data);
        setReassignmentDialog(true);
        return false;
      }

      // Check for warnings (expired documents)
      if (response.data.warnings) {
        setWarningData(response.data);
        setWarningDialog(true);
        return false;
      }

      if (response.data.error) {
        toast.error(response.data.error);
        return false;
      }

      return false;
    } catch (error) {
      toast.error('Failed to validate bike assignment');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission triggered', formData);
    
    // Validate all required fields
    const errors = [];
    
    // Personal Info validation
    if (!formData.name || !formData.name.trim()) {
      errors.push('Name is required');
    }
    if (!formData.date_of_birth) {
      errors.push('Date of Birth is required');
    }
    if (!formData.email || !formData.email.trim()) {
      errors.push('Email is required');
    }
    if (!formData.mobile_uae || !formData.mobile_uae.trim()) {
      errors.push('UAE Mobile is required');
    }
    
    // Document validation
    if (!formData.emirates_id || !formData.emirates_id.trim()) {
      errors.push('Emirates ID is required');
    }
    if (!formData.emirates_id_expiry) {
      errors.push('Emirates ID Expiry is required');
    }
    if (!formData.passport_number || !formData.passport_number.trim()) {
      errors.push('Passport Number is required');
    }
    if (!formData.passport_expiry) {
      errors.push('Passport Expiry is required');
    }
    if (!formData.license_number || !formData.license_number.trim()) {
      errors.push('Driving License Number is required');
    }
    if (!formData.license_expiry) {
      errors.push('License Expiry is required');
    }
    
    if (errors.length > 0) {
      // Show first 3 errors
      const displayErrors = errors.slice(0, 3);
      displayErrors.forEach(err => toast.error(err));
      if (errors.length > 3) {
        toast.error(`...and ${errors.length - 3} more required fields`);
      }
      return;
    }
    
    // Validate bike assignment
    const isValid = await validateBikeAssignment();
    if (!isValid) {
      return; // Validation dialogs will handle next steps
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API}/riders/${editingId}`, formData, { 
          headers: getAuthHeader(),
          timeout: 30000 // 30 second timeout
        });
        toast.success('Rider updated');
      } else {
        await axios.post(`${API}/riders`, formData, { 
          headers: getAuthHeader(),
          timeout: 30000 // 30 second timeout
        });
        toast.success('Rider added successfully!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      let errorMessage = 'Operation failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
        
        // Check if it's a duplicate Emirates ID error
        if (errorMessage.includes('Duplicate Emirates ID') || error.response.status === 409) {
          toast.error('Rider already exists with this Emirates ID', {
            duration: 4000,
            style: {
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              color: '#92400E'
            }
          });
          // Close dialog and return to rider list
          setDialogOpen(false);
          resetForm();
          fetchData(); // Refresh the list to show the existing rider
          return;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error with longer duration for other errors
      toast.error(errorMessage, {
        duration: 5000,
        style: {
          background: '#FEE2E2',
          border: '1px solid #DC2626',
          color: '#991B1B'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReassignmentConfirm = async () => {
    setReassignmentDialog(false);
    // Proceed to check for warnings after reassignment confirmed
    try {
      const response = await axios.post(
        `${API}/riders/validate-bike-assignment`,
        {
          bike_id: formData.assigned_bike_id,
          rider_id: editingId
        },
        { headers: getAuthHeader() }
      );

      if (response.data.warnings) {
        setWarningData(response.data);
        setWarningDialog(true);
      } else {
        // No warnings, proceed with assignment
        await proceedWithAssignment();
      }
    } catch (error) {
      toast.error('Failed to validate bike assignment');
    }
  };

  const handleOverruleWarning = async () => {
    setWarningDialog(false);
    
    try {
      // Log the overrule and assign bike
      const warningTypes = warningData.warnings.map(w => w.type);
      await axios.post(
        `${API}/riders/assign-bike-with-overrule`,
        {
          rider_id: editingId || formData.id,
          bike_id: formData.assigned_bike_id,
          warning_types: warningTypes,
          notes: overruleNotes
        },
        { headers: getAuthHeader() }
      );

      // Now save the rider
      await proceedWithAssignment();
      toast.warning('Bike assigned with warnings. Please correct within 3 working days.');
    } catch (error) {
      toast.error('Failed to assign bike with overrule');
    }
  };

  const proceedWithAssignment = async () => {
    try {
      if (editingId) {
        await axios.put(`${API}/riders/${editingId}`, formData, { headers: getAuthHeader() });
        toast.success('Rider updated');
      } else {
        await axios.post(`${API}/riders`, formData, { headers: getAuthHeader() });
        toast.success('Rider added');
      }
      setDialogOpen(false);
      resetForm();
      setOverruleNotes('');
      fetchData();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleLesseeChange = (lesseeId) => {
    const lessee = lessees.find(l => l.id === lesseeId);
    if (lessee && lessee.lessee_type === 'in-house') {
      toast.error('This is In-house lessee rider, please add through TAP page');
      setFormData({
        ...formData,
        lessee_id: '',
        lessee_name: ''
      });
      return;
    }
    setFormData({
      ...formData,
      lessee_id: lesseeId,
      lessee_name: lessee ? lessee.company_name : ''
    });
  };

  const handleEdit = (rider) => {
    setEditingId(rider.id);
    setUseSequentialWizard(false); // Disable wizard for editing
    setShowRiderForm(false); // Will show edit form directly
    setFormData({ 
      name: rider.name,
      date_of_birth: formatDateForInput(rider.date_of_birth),
      email: rider.email,
      mobile_uae: rider.mobile_uae,
      mobile_home_country: rider.mobile_home_country || '',
      nationality: rider.nationality || '',
      emirates_id: rider.emirates_id,
      emirates_id_issue_date: formatDateForInput(rider.emirates_id_issue_date),
      emirates_id_expiry: formatDateForInput(rider.emirates_id_expiry),
      employer: rider.employer || '',
      passport_number: rider.passport_number,
      passport_issue_date: formatDateForInput(rider.passport_issue_date),
      passport_expiry: formatDateForInput(rider.passport_expiry),
      license_number: rider.license_number,
      license_issue_date: formatDateForInput(rider.license_issue_date),
      license_expiry: formatDateForInput(rider.license_expiry),
      visa_number: rider.visa_number || '',
      visa_expiry_date: formatDateForInput(rider.visa_expiry_date),
      labour_card_no: rider.labour_card_no || '',
      labour_card_expiry_date: formatDateForInput(rider.labour_card_expiry_date),
      person_code_no: rider.person_code_no || '',
      uid_no: rider.uid_no || '',
      file_no: rider.file_no || '',
      file_issue_date: formatDateForInput(rider.file_issue_date),
      file_expiry_date: formatDateForInput(rider.file_expiry_date),
      platform: rider.platform,
      platform_id: rider.platform_id || '',
      custom_platform: rider.custom_platform || '',
      status: rider.status,
      lessee_id: rider.lessee_id || '',
      lessee_name: rider.lessee_name || '',
      assigned_bike_id: rider.assigned_bike_id || '',
      assigned_bike_plate: rider.assigned_bike_plate || '',
      photo_url: rider.photo_url || '',
      emirates_id_doc_url: rider.emirates_id_doc_url || '',
      passport_doc_url: rider.passport_doc_url || '',
      license_doc_url: rider.license_doc_url || '',
      designation: rider.designation || 'Motorcyclist',
      edenred_id: rider.edenred_id || '',
      ohc_number: rider.ohc_number || '',
      ohc_expiry_date: formatDateForInput(rider.ohc_expiry_date),
      food_permit_number: rider.food_permit_number || '',
      food_permit_expiry_date: formatDateForInput(rider.food_permit_expiry_date)
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`${API}/riders/${id}`, { headers: getAuthHeader() });
        toast.success('Rider deleted');
        fetchRiders();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  // Bulk document upload handlers
  const handleBulkDocFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        toast.error('Please select a ZIP file');
        return;
      }
      setBulkDocFile(file);
      setBulkDocResults(null);
    }
  };

  const handleBulkDocUpload = async () => {
    if (!bulkDocFile) {
      toast.error('Please select a ZIP file to upload');
      return;
    }

    setBulkDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkDocFile);

      const response = await axios.post(
        `${API}/riders/bulk-upload-documents`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setBulkDocResults(response.data);
      
      if (response.data.processed_riders > 0) {
        toast.success(`Successfully uploaded documents for ${response.data.processed_riders} riders`);
        fetchRiders();
      }
      
      if (response.data.not_found_riders?.length > 0) {
        toast.warning(`${response.data.not_found_riders.length} rider(s) not found in system`);
      }
      
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors occurred during upload`);
      }
    } catch (error) {
      console.error('Bulk document upload error:', error);
      toast.error(formatErrorMessage(error, 'Failed to upload documents'));
    } finally {
      setBulkDocUploading(false);
    }
  };

  const downloadBulkUploadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/riders/bulk-upload-template`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Rider_Bulk_Upload_Template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const resetBulkDocDialog = () => {
    setBulkDocFile(null);
    setBulkDocResults(null);
    setBulkDocDialogOpen(false);
  };

  const resetForm = () => {
    setFormData({ 
      name: '', date_of_birth: '', email: '', mobile_uae: '', mobile_home_country: '',
      nationality: '', emirates_id: '', emirates_id_issue_date: '', emirates_id_expiry: '', employer: '',
      passport_number: '', passport_issue_date: '', passport_expiry: '',
      license_number: '', license_issue_date: '', license_expiry: '', 
      platform: '', platform_id: '', custom_platform: '', status: 'active',
      lessee_id: '', lessee_name: '',
      assigned_bike_id: '', assigned_bike_plate: '',
      photo_url: '', emirates_id_doc_url: '', passport_doc_url: '', license_doc_url: '',
      designation: 'Motorcyclist',
      edenred_id: '', ohc_number: '', ohc_expiry_date: '', food_permit_number: '', food_permit_expiry_date: ''
    });
    setEditingId(null);
    setOverruleNotes('');
    setCurrentStep(1);
    setOcrProcessing(false);
    setOcrResults(null);
    setAutoFilled(false);
    setScannedDocumentType(null);
    setUploadedDocuments({
      license: null,
      emirates_id: null,
      passport: null
    });
    setDocumentNames({
      license: {},
      emirates_id: {},
      passport: {}
    });
    setIndividualAutoFilled({
      name: false,
      date_of_birth: false,
      nationality: false,
      emirates_id: false,
      emirates_id_issue_date: false,
      emirates_id_expiry: false,
      employer: false,
      passport_number: false,
      passport_issue_date: false,
      passport_expiry: false,
      license_number: false,
      license_issue_date: false,
      license_expiry: false
    });
    setUseSequentialWizard(false);
    setShowRiderForm(false);
    setActiveFormTab('personal'); // Reset to first tab
    setIsSubmitting(false); // Reset submitting state
  };

  // View rider history
  const handleViewHistory = async (rider) => {
    setHistoryRider(rider);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    
    try {
      const response = await axios.get(
        `${API}/riders/${rider.id}/history`,
        { headers: getAuthHeader() }
      );
      setRiderHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch rider history:', error);
      toast.error('Failed to load rider history');
      setRiderHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle wizard completion - merge data into formData and show final form
  const handleWizardComplete = (wizardData) => {
    // Update formData with wizard data
    setFormData(prev => ({
      ...prev,
      ...wizardData
    }));
    
    // Hide wizard, show final form
    setUseSequentialWizard(false);
    setShowRiderForm(true);
    
    toast.success('Documents uploaded successfully! Please complete the remaining details.');
  };

  // Multi-step navigation
  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
      setAutoFilled(false);
      setOcrResults(null);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedToNextStep = () => {
    switch(currentStep) {
      case 1: // Emirates ID step - can proceed if uploaded (OCR success not required)
        return uploadedDocuments.emirates_id !== null;
      case 2: // Passport step - can proceed if uploaded (OCR success not required)
        return uploadedDocuments.passport !== null;
      case 3: // License step - can proceed if uploaded (OCR success not required)
        return uploadedDocuments.license !== null;
      default:
        return true;
    }
  };

  const handleViewDetails = (rider) => {
    setSelectedRider(rider);
    setDetailDialogOpen(true);
    setRiderDetailTab('info'); // Reset to info tab
    // Fetch platform history for this rider
    fetchPlatformHistory(rider.id);
    // Fetch vehicle, fines, and maintenance history
    fetchVehicleHistory(rider.id);
    fetchFinesHistory(rider.id);
    fetchMaintenanceHistory(rider.id);
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      // For now, store file name. In production, upload to cloud storage
      setFormData({ ...formData, [fieldName]: file.name });
      toast.success(`${file.name} ready for upload`);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96">Loading...</div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="riders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('riders.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('riders.subtitle')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={dialogOpen} onOpenChange={(open) => { 
            setDialogOpen(open); 
            if (!open) resetForm(); 
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-lime-600 to-green-600" 
                data-testid="add-rider-btn"
                size={isMobile ? "sm" : "default"}
                onClick={() => {
                  setUseSequentialWizard(true);
                  setShowRiderForm(false);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />{isMobile ? 'Add' : t('riders.addRider')}
              </Button>
            </DialogTrigger>
          <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>{editingId ? t('riders.editRider') : t('riders.addRider')}</DialogTitle>
              <DialogDescription>
                {editingId ? t('riders.subtitle') : 'Follow the steps to add a new rider with document verification'}
              </DialogDescription>
            </DialogHeader>

            {/* Show Sequential Wizard for New Rider */}
            {!editingId && useSequentialWizard && !showRiderForm && (
              <div className="space-y-4">
                {/* Skip OCR Option */}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUseSequentialWizard(false);
                      setShowRiderForm(true);
                      toast.info('Entering manual mode. Please fill in all rider details.');
                    }}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    data-testid="skip-ocr-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Skip & Add Details Manually
                  </Button>
                </div>
                
                <DocumentUploadWizard 
                  onComplete={handleWizardComplete}
                  onCancel={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  editingId={editingId}
                  lessees={lessees}
                  onSkipToManual={() => {
                    setUseSequentialWizard(false);
                    setShowRiderForm(true);
                    toast.info('Entering manual mode. Please fill in all rider details.');
                  }}
                />
              </div>
            )}

            {/* Show Final Form After Wizard or For Editing */}
            {(editingId || showRiderForm) && (
              <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              
              {/* Edit mode - show OCR upload section */}
              {editingId && (
                // Edit mode - show traditional tabbed form
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <Camera className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">Upload Rider Documents for Auto-Fill</h3>
                    {autoFilled && (
                      <div className="flex items-center gap-1 ml-auto">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-700 font-medium">Auto-filled from {scannedDocumentType === 'license' ? 'License' : scannedDocumentType === 'emirates_id' ? 'Emirates ID' : 'Passport'}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-purple-700">
                    Upload one of the following documents to automatically fill rider information: UAE Driving License, Emirates ID, or Passport
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> This OCR feature uses Tesseract.js to scan and extract text from your documents. Processing may take 10-30 seconds. Please verify extracted information and use individual &ldquo;Clear&rdquo; buttons to correct any fields if needed.
                    </p>
                  </div>
                
                  {/* Document Type Selection & Upload */}
                  <div className="grid grid-cols-3 gap-3">
                  {/* UAE Driving License */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      id="license-upload"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const tempUrl = URL.createObjectURL(file);
                            setUploadedDocuments(prev => ({...prev, license: file}));
                            await processRiderDocumentOCR(tempUrl, 'license', file);
                          } catch (error) {
                            toast.error('Failed to process file');
                          }
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                    
                    {ocrProcessing && scannedDocumentType === 'license' ? (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <Loader className="w-6 h-6 text-purple-500 animate-spin" />
                        <div className="text-xs font-medium text-purple-700">Scanning...</div>
                      </div>
                    ) : uploadedDocuments.license ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                        <div className="text-xs text-gray-700 font-medium">License Uploaded</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUploadedDocument('license')}
                          className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <FileText className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <label 
                          htmlFor="license-upload"
                          className="cursor-pointer text-xs text-purple-600 hover:text-purple-700 font-medium block"
                        >
                          UAE Driving License
                        </label>
                      </>
                    )}
                  </div>

                  {/* Emirates ID */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      id="emirates-id-upload"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const tempUrl = URL.createObjectURL(file);
                            setUploadedDocuments(prev => ({...prev, emirates_id: file}));
                            await processRiderDocumentOCR(tempUrl, 'emirates_id', file);
                          } catch (error) {
                            toast.error('Failed to process file');
                          }
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                    
                    {ocrProcessing && scannedDocumentType === 'emirates_id' ? (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <Loader className="w-6 h-6 text-purple-500 animate-spin" />
                        <div className="text-xs font-medium text-purple-700">Scanning...</div>
                      </div>
                    ) : uploadedDocuments.emirates_id ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                        <div className="text-xs text-gray-700 font-medium">Emirates ID Uploaded</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUploadedDocument('emirates_id')}
                          className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <CreditCard className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <label 
                          htmlFor="emirates-id-upload"
                          className="cursor-pointer text-xs text-purple-600 hover:text-purple-700 font-medium block"
                        >
                          Emirates ID
                        </label>
                      </>
                    )}
                  </div>

                  {/* Passport */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      id="passport-upload"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const tempUrl = URL.createObjectURL(file);
                            setUploadedDocuments(prev => ({...prev, passport: file}));
                            await processRiderDocumentOCR(tempUrl, 'passport', file);
                          } catch (error) {
                            toast.error('Failed to process file');
                          }
                        }
                        e.target.value = ''; // Reset input
                      }}
                    />
                    
                    {ocrProcessing && scannedDocumentType === 'passport' ? (
                      <div className="flex flex-col items-center gap-2 py-2">
                        <Loader className="w-6 h-6 text-purple-500 animate-spin" />
                        <div className="text-xs font-medium text-purple-700">Scanning...</div>
                      </div>
                    ) : uploadedDocuments.passport ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                        <div className="text-xs text-gray-700 font-medium">Passport Uploaded</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUploadedDocument('passport')}
                          className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <>
                        <File className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <label 
                          htmlFor="passport-upload"
                          className="cursor-pointer text-xs text-purple-600 hover:text-purple-700 font-medium block"
                        >
                          Passport
                        </label>
                      </>
                    )}
                  </div>
                </div>

                {/* OCR Results Summary */}
                {ocrResults && (
                  <div className="bg-white p-3 rounded border border-green-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Extracted Information:</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {ocrResults.name && (
                        <div><span className="text-gray-500">Name:</span> {ocrResults.name}</div>
                      )}
                      {ocrResults.date_of_birth && (
                        <div><span className="text-gray-500">Date of Birth:</span> {formatDateDisplay(ocrResults.date_of_birth)}</div>
                      )}
                      {ocrResults.emirates_id && (
                        <div><span className="text-gray-500">Emirates ID:</span> {ocrResults.emirates_id}</div>
                      )}
                      {ocrResults.license_number && (
                        <div><span className="text-gray-500">License:</span> {ocrResults.license_number}</div>
                      )}
                      {ocrResults.passport_number && (
                        <div><span className="text-gray-500">Passport:</span> {ocrResults.passport_number}</div>
                      )}
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* Show traditional tabbed form in edit mode or after wizard completion */}
              {(editingId || showRiderForm) && (
                <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="personal">Personal Info</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="additional">Additional Info</TabsTrigger>
                  <TabsTrigger value="review">Review</TabsTrigger>
                </TabsList>

                <TabsContent value="personal" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>{t('riders.name')} *</Label>
                          {individualAutoFilled.name && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.name && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('name')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        value={formData.name} 
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                        required 
                        data-testid="rider-name"
                        className={individualAutoFilled.name ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Date of Birth *</Label>
                          {individualAutoFilled.date_of_birth && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.date_of_birth && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('date_of_birth')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.date_of_birth} 
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} 
                        required 
                        data-testid="rider-dob"
                        className={individualAutoFilled.date_of_birth ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Nationality</Label>
                          {individualAutoFilled.nationality && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.nationality && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('nationality')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        placeholder="e.g., Pakistani, Indian, Filipino" 
                        value={formData.nationality} 
                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                        data-testid="rider-nationality"
                        className={individualAutoFilled.nationality ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('riders.email')} *</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="rider-email" />
                    </div>
                    <div className="space-y-2">
                      <Label>UAE Mobile No. *</Label>
                      <Input placeholder="+971 50 123 4567" value={formData.mobile_uae} onChange={(e) => setFormData({ ...formData, mobile_uae: e.target.value })} required readOnly={!!editingId} data-testid="rider-mobile-uae" className={editingId ? "bg-slate-100 cursor-not-allowed" : ""} title={editingId ? "Locked — Mobile is managed in TAP" : undefined} />
                      {editingId && (
                        <p className="text-xs text-slate-500 mt-1">🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a></p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Home Country Mobile No.</Label>
                      <Input placeholder="+92 300 1234567" value={formData.mobile_home_country} onChange={(e) => setFormData({ ...formData, mobile_home_country: e.target.value })} data-testid="rider-mobile-home" />
                    </div>
                    <div className="space-y-2">
                      <Label>Platform *</Label>
                      <SearchableSelect
                        options={[
                          { value: 'Noon', label: 'Noon' },
                          { value: 'Talabat', label: 'Talabat' },
                          { value: 'Keeta', label: 'Keeta' },
                          { value: 'Careem', label: 'Careem' },
                          { value: 'Deliveroo', label: 'Deliveroo' },
                          { value: 'Zomato', label: 'Zomato' },
                          { value: 'Other', label: 'Other' }
                        ]}
                        value={formData.platform}
                        onValueChange={(value) => setFormData({ ...formData, platform: value, custom_platform: value === 'Other' ? formData.custom_platform : '' })}
                        placeholder="Select platform"
                        showMetadata={false}
                      />
                    </div>
                    {formData.platform && (
                      <div className="space-y-2">
                        <Label>Platform ID</Label>
                        <Input 
                          placeholder={`Enter ${formData.platform === 'Other' && formData.custom_platform ? formData.custom_platform : formData.platform} ID`}
                          value={formData.platform_id} 
                          onChange={(e) => setFormData({ ...formData, platform_id: e.target.value })} 
                          data-testid="rider-platform-id"
                        />
                        <p className="text-xs text-slate-500">Platform-specific rider ID (e.g., Talabat ID, Noon ID)</p>
                      </div>
                    )}
                    {formData.platform === 'Other' && (
                      <div className="space-y-2">
                        <Label>Custom Platform Name *</Label>
                        <Input 
                          placeholder="Enter platform name" 
                          value={formData.custom_platform} 
                          onChange={(e) => setFormData({ ...formData, custom_platform: e.target.value })} 
                          required 
                          data-testid="rider-custom-platform"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                        <SelectTrigger data-testid="rider-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Designation</Label>
                      <Select value={formData.designation} onValueChange={(value) => setFormData({ ...formData, designation: value })}>
                        <SelectTrigger data-testid="rider-designation"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Motorcyclist">Motorcyclist</SelectItem>
                          <SelectItem value="Cyclist">Cyclist</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Type of vehicle the rider operates</p>
                    </div>
                    
                    {/* Additional IDs Section */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Additional IDs (Optional)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Edenred ID</Label>
                          <Input 
                            placeholder="Enter Edenred ID"
                            value={formData.edenred_id} 
                            onChange={(e) => setFormData({ ...formData, edenred_id: e.target.value })} 
                            data-testid="rider-edenred-id"
                          />
                        </div>
                        <div></div>
                        <div className="space-y-2">
                          <Label>OHC Number</Label>
                          <Input 
                            placeholder="Occupational Health Card No."
                            value={formData.ohc_number} 
                            onChange={(e) => setFormData({ ...formData, ohc_number: e.target.value })} 
                            data-testid="rider-ohc-number"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>OHC Expiry Date</Label>
                          <Input 
                            type="date"
                            value={formData.ohc_expiry_date} 
                            onChange={(e) => setFormData({ ...formData, ohc_expiry_date: e.target.value })} 
                            data-testid="rider-ohc-expiry"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Food Permit No.</Label>
                          <Input 
                            placeholder="Enter Food Permit No."
                            value={formData.food_permit_number} 
                            onChange={(e) => setFormData({ ...formData, food_permit_number: e.target.value })} 
                            data-testid="rider-food-permit"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Food Permit Expiry Date</Label>
                          <Input 
                            type="date"
                            value={formData.food_permit_expiry_date} 
                            onChange={(e) => setFormData({ ...formData, food_permit_expiry_date: e.target.value })} 
                            data-testid="rider-food-permit-expiry"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Lessee *</Label>
                      {dialogOpen && console.log('Dialog open - lessees:', lessees.length)}
                      <SearchableSelect
                        key={`lessee-select-${lessees.length}`}
                        options={lessees.map((lessee) => ({
                          value: lessee.id,
                          label: lessee.company_name,
                          searchText: `${lessee.company_name} ${lessee.owner_name || ''} ${lessee.trade_license_number || ''} ${lessee.trn_number || ''}`,
                          metadata: {
                            'Owner': lessee.owner_name,
                            'TRN': lessee.trn_number || lessee.trn_no,
                            'Trade License': lessee.trade_license_number || lessee.trade_license_no
                          }
                        }))}
                        value={formData.lessee_id}
                        onValueChange={handleLesseeChange}
                        placeholder="Select lessee"
                        emptyMessage="No lessees found"
                      />
                      <p className="text-xs text-gray-500">Found {lessees.length} lessees (Refresh if zero)</p>
                      {lessees.length === 0 && (
                        <button 
                          type="button"
                          onClick={() => fetchData()} 
                          className="text-xs text-blue-600 underline"
                        >
                          Click to reload lessees
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Assign a Motorcycle</Label>
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="motorcycle-assignment-locked">
                        <p className="text-sm text-amber-800">
                          This feature is temporarily locked. Kindly visit Motorcycle page to assign a Motorcycle for the above Rider.{' '}
                          <a
                            href="/motorcycles"
                            className="text-blue-600 underline font-medium hover:text-blue-800"
                            data-testid="motorcycle-page-link"
                          >
                            Click here
                          </a>
                        </p>
                      </div>
                      {formData.assigned_bike_plate && (
                        <p className="text-xs text-gray-600">Currently assigned: {formData.assigned_bike_plate}</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="documents" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Emirates ID No. *</Label>
                          {individualAutoFilled.emirates_id && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.emirates_id && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('emirates_id')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        placeholder="784-1234-1234567-1" 
                        value={formData.emirates_id} 
                        onChange={(e) => setFormData({ ...formData, emirates_id: e.target.value })} 
                        required 
                        readOnly={!!editingId}
                        data-testid="rider-emirates-id"
                        className={`${individualAutoFilled.emirates_id ? "border-green-300 bg-green-50" : ""} ${editingId ? "bg-slate-100 cursor-not-allowed" : ""}`}
                        title={editingId ? "Locked — Emirates ID is managed in TAP" : undefined}
                      />
                      {editingId && (
                        <p className="text-xs text-slate-500 mt-1">
                          🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a>
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Emirates ID Expiry *</Label>
                          {individualAutoFilled.emirates_id_expiry && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.emirates_id_expiry && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('emirates_id_expiry')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.emirates_id_expiry} 
                        onChange={(e) => setFormData({ ...formData, emirates_id_expiry: e.target.value })} 
                        required 
                        data-testid="rider-emirates-expiry"
                        className={individualAutoFilled.emirates_id_expiry ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Emirates ID Issue Date</Label>
                          {individualAutoFilled.emirates_id_issue_date && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.emirates_id_issue_date && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('emirates_id_issue_date')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.emirates_id_issue_date} 
                        onChange={(e) => setFormData({ ...formData, emirates_id_issue_date: e.target.value })}
                        data-testid="rider-emirates-issue"
                        className={individualAutoFilled.emirates_id_issue_date ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Employer/Sponsor</Label>
                          {individualAutoFilled.employer && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.employer && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('employer')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        placeholder="Company name" 
                        value={formData.employer} 
                        onChange={(e) => setFormData({ ...formData, employer: e.target.value })}
                        data-testid="rider-employer"
                        className={individualAutoFilled.employer ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Passport No. *</Label>
                          {individualAutoFilled.passport_number && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.passport_number && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('passport_number')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        placeholder="AB1234567" 
                        value={formData.passport_number} 
                        onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })} 
                        required 
                        readOnly={!!editingId}
                        data-testid="rider-passport"
                        className={`${individualAutoFilled.passport_number ? "border-green-300 bg-green-50" : ""} ${editingId ? "bg-slate-100 cursor-not-allowed" : ""}`}
                        title={editingId ? "Locked — Passport is managed in TAP" : undefined}
                      />
                      {editingId && (
                        <p className="text-xs text-slate-500 mt-1">
                          🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a>
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Passport Expiry *</Label>
                          {individualAutoFilled.passport_expiry && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.passport_expiry && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('passport_expiry')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.passport_expiry} 
                        onChange={(e) => setFormData({ ...formData, passport_expiry: e.target.value })} 
                        required 
                        data-testid="rider-passport-expiry"
                        className={individualAutoFilled.passport_expiry ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Passport Issue Date</Label>
                          {individualAutoFilled.passport_issue_date && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.passport_issue_date && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('passport_issue_date')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.passport_issue_date} 
                        onChange={(e) => setFormData({ ...formData, passport_issue_date: e.target.value })}
                        data-testid="rider-passport-issue"
                        className={individualAutoFilled.passport_issue_date ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Driving License No. *</Label>
                          {individualAutoFilled.license_number && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.license_number && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('license_number')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        placeholder="DL-123456" 
                        value={formData.license_number} 
                        onChange={(e) => setFormData({ ...formData, license_number: e.target.value })} 
                        required 
                        data-testid="rider-license"
                        className={individualAutoFilled.license_number ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>License Expiry *</Label>
                          {individualAutoFilled.license_expiry && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.license_expiry && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('license_expiry')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.license_expiry} 
                        onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })} 
                        required 
                        data-testid="rider-license-expiry"
                        className={individualAutoFilled.license_expiry ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>License Issue Date</Label>
                          {individualAutoFilled.license_issue_date && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Auto-filled</span>
                          )}
                        </div>
                        {individualAutoFilled.license_issue_date && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => clearIndividualField('license_issue_date')}
                            className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>
                      <Input 
                        type="date" 
                        value={formData.license_issue_date} 
                        onChange={(e) => setFormData({ ...formData, license_issue_date: e.target.value })}
                        data-testid="rider-license-issue"
                        className={individualAutoFilled.license_issue_date ? "border-green-300 bg-green-50" : ""}
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* Additional Info Tab - Labour Card, File Information */}
                <TabsContent value="additional" className="space-y-4 mt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 font-medium">Optional: Labour Card & File Information</p>
                    <p className="text-xs text-blue-600 mt-1">These fields are not mandatory but help with complete rider documentation.</p>
                  </div>
                  
                  {/* Visa Information */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">Visa Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Visa Number</Label>
                        <Input 
                          value={formData.visa_number} 
                          onChange={(e) => setFormData({ ...formData, visa_number: e.target.value })}
                          placeholder="Enter visa number"
                          data-testid="rider-visa-number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Visa Expiry Date</Label>
                        <Input 
                          type="date" 
                          value={formData.visa_expiry_date} 
                          onChange={(e) => setFormData({ ...formData, visa_expiry_date: e.target.value })}
                          data-testid="rider-visa-expiry"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Labour Card Information */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">Labour Card Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Labour Card No.</Label>
                        <Input 
                          value={formData.labour_card_no} 
                          onChange={(e) => setFormData({ ...formData, labour_card_no: e.target.value })}
                          placeholder="Enter labour card number"
                          data-testid="rider-labour-card-no"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Labour Card Expiry Date</Label>
                        <Input 
                          type="date" 
                          value={formData.labour_card_expiry_date} 
                          onChange={(e) => setFormData({ ...formData, labour_card_expiry_date: e.target.value })}
                          data-testid="rider-labour-card-expiry"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Additional IDs */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">Additional Identifiers</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Person Code No.</Label>
                        <Input 
                          value={formData.person_code_no} 
                          onChange={(e) => setFormData({ ...formData, person_code_no: e.target.value })}
                          placeholder="Person code"
                          data-testid="rider-person-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>UID No.</Label>
                        <Input 
                          value={formData.uid_no} 
                          onChange={(e) => setFormData({ ...formData, uid_no: e.target.value })}
                          placeholder="UID number"
                          data-testid="rider-uid-no"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>File No.</Label>
                        <Input 
                          value={formData.file_no} 
                          onChange={(e) => setFormData({ ...formData, file_no: e.target.value })}
                          placeholder="File number"
                          data-testid="rider-file-no"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* File Dates */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-700">File Dates</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>File Issue Date</Label>
                        <Input 
                          type="date" 
                          value={formData.file_issue_date} 
                          onChange={(e) => setFormData({ ...formData, file_issue_date: e.target.value })}
                          data-testid="rider-file-issue-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>File Expiry Date</Label>
                        <Input 
                          type="date" 
                          value={formData.file_expiry_date} 
                          onChange={(e) => setFormData({ ...formData, file_expiry_date: e.target.value })}
                          data-testid="rider-file-expiry-date"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Review Tab */}
                <TabsContent value="review" className="space-y-4 mt-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800 font-medium">✅ Review all information before submitting</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Personal Information</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><span className="text-slate-500">Name:</span> {formData.name || '-'}</p>
                        <p><span className="text-slate-500">DOB:</span> {formData.date_of_birth || '-'}</p>
                        <p><span className="text-slate-500">Nationality:</span> {formData.nationality || '-'}</p>
                        <p><span className="text-slate-500">Email:</span> {formData.email || '-'}</p>
                        <p><span className="text-slate-500">UAE Mobile:</span> {formData.mobile_uae || '-'}</p>
                        <p><span className="text-slate-500">Platform:</span> {formData.platform || '-'}</p>
                        <p><span className="text-slate-500">Status:</span> {formData.status || '-'}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Documents</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm space-y-1">
                        <p><span className="text-slate-500">Emirates ID:</span> {formData.emirates_id || '-'}</p>
                        <p><span className="text-slate-500">Emirates ID Expiry:</span> {formData.emirates_id_expiry || '-'}</p>
                        <p><span className="text-slate-500">Passport:</span> {formData.passport_number || '-'}</p>
                        <p><span className="text-slate-500">Passport Expiry:</span> {formData.passport_expiry || '-'}</p>
                        <p><span className="text-slate-500">License:</span> {formData.license_number || '-'}</p>
                        <p><span className="text-slate-500">License Expiry:</span> {formData.license_expiry || '-'}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {formData.assigned_bike && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Assigned Motorcycle</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm">
                        <p><span className="text-slate-500">Plate Number:</span> {formData.assigned_bike}</p>
                        {formData.assigned_lessee && <p><span className="text-slate-500">Lessee:</span> {formData.assigned_lessee}</p>}
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>
              )}

              {/* Form Navigation Buttons - only show when form tabs are visible */}
              {(editingId || showRiderForm) && (
              <div className="flex gap-2 justify-end mt-6">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                
                {/* Show Back button on Documents and Review tabs */}
                {activeFormTab !== 'personal' && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      if (activeFormTab === 'documents') setActiveFormTab('personal');
                      else if (activeFormTab === 'review') setActiveFormTab('documents');
                    }}
                  >
                    Back
                  </Button>
                )}
                
                {/* Show Next button on Personal Info and Documents tabs */}
                {activeFormTab !== 'review' && (
                  <Button 
                    type="button"
                    className="bg-gradient-to-r from-blue-600 to-purple-600"
                    onClick={() => {
                      if (activeFormTab === 'personal') setActiveFormTab('documents');
                      else if (activeFormTab === 'documents') setActiveFormTab('review');
                    }}
                  >
                    Next
                  </Button>
                )}
                
                {/* Show Add/Update Rider button only on Review tab */}
                {activeFormTab === 'review' && (
                  <Button 
                    type="submit" 
                    className="bg-gradient-to-r from-lime-600 to-green-600" 
                    data-testid="submit-rider-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : (editingId ? 'Update' : 'Add')} {!isSubmitting && 'Rider'}
                  </Button>
                )}
              </div>
              )}
            </form>
            )}
          </DialogContent>
        </Dialog>
        
        {permissions.canCreate && (
          <>
            {/* Bulk Upload Riders with Dropdown */}
            <BulkUpload
              title="Bulk Upload Riders"
              downloadTemplate={downloadBulkUploadTemplate}
              uploadEndpoint="/riders/bulk-upload"
              confirmEndpoint="/riders/bulk-confirm"
              onSuccess={fetchRiders}
              getAuthHeader={getAuthHeader}
              API={API}
              useDropdown={true}
              additionalMenuItems={[
                {
                  label: 'Bulk Upload Documents',
                  icon: <FileText className="w-3 h-3 mr-2" />,
                  onClick: () => setBulkDocDialogOpen(true),
                  testId: 'bulk-upload-docs-btn'
                }
              ]}
            />
            
            {/* Bulk Upload Documents Dialog - Hidden trigger, opened via menu */}
            <Dialog open={bulkDocDialogOpen} onOpenChange={(open) => { if (!open) resetBulkDocDialog(); setBulkDocDialogOpen(open); }}>
              <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Bulk Upload Rider Documents
                  </DialogTitle>
                  <DialogDescription>
                    Upload documents for multiple riders at once using a nested ZIP file structure.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Instructions */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">📁 ZIP File Structure:</h4>
                    <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`Master_Documents.zip
├── 784-1234-5678901-1.zip    (Emirates ID)
│   ├── Emirates ID Front.jpg
│   ├── Emirates ID Back.jpg
│   ├── Passport.jpg
│   └── Driving License.jpg
├── 784-2345-6789012-2.zip    (Emirates ID)
│   └── ...`}
                    </pre>
                    <div className="mt-3 text-sm text-amber-700 space-y-1">
                      <p>• Each rider's ZIP file must be named with their <strong>Emirates ID</strong></p>
                      <p>• File names should contain: "Emirates ID Front", "Emirates ID Back", "Passport", "Driving License"</p>
                      <p>• Supported formats: .jpg, .jpeg, .png, .pdf</p>
                    </div>
                  </div>
                  
                  {/* File Upload */}
                  <div className="space-y-2">
                    <Label>Select Master ZIP File</Label>
                    <Input
                      type="file"
                      accept=".zip"
                      onChange={handleBulkDocFileChange}
                      className="cursor-pointer"
                    />
                    {bulkDocFile && (
                      <p className="text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        {bulkDocFile.name} ({(bulkDocFile.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    )}
                  </div>
                  
                  {/* Results */}
                  {bulkDocResults && (
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg ${bulkDocResults.processed_riders > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                        <h4 className="font-semibold text-green-800">✅ Processed: {bulkDocResults.processed_riders} riders</h4>
                        <div className="text-sm text-green-700 mt-1">
                          <p>Documents uploaded:</p>
                          <ul className="list-disc list-inside ml-2">
                            <li>Emirates ID Front: {bulkDocResults.document_counts?.emirates_id_front || 0}</li>
                            <li>Emirates ID Back: {bulkDocResults.document_counts?.emirates_id_back || 0}</li>
                            <li>Passport: {bulkDocResults.document_counts?.passport || 0}</li>
                            <li>Driving License: {bulkDocResults.document_counts?.license || 0}</li>
                          </ul>
                        </div>
                      </div>
                      
                      {bulkDocResults.not_found_riders?.length > 0 && (
                        <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                          <h4 className="font-semibold text-yellow-800">⚠️ Riders Not Found: {bulkDocResults.not_found_riders.length}</h4>
                          <p className="text-sm text-yellow-700 mt-1">
                            These Emirates IDs were not found in the system:
                          </p>
                          <div className="text-xs text-yellow-600 mt-1 max-h-20 overflow-y-auto">
                            {bulkDocResults.not_found_riders.join(', ')}
                          </div>
                        </div>
                      )}
                      
                      {bulkDocResults.errors?.length > 0 && (
                        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                          <h4 className="font-semibold text-red-800">❌ Errors: {bulkDocResults.errors.length}</h4>
                          <div className="text-xs text-red-600 mt-1 max-h-20 overflow-y-auto">
                            {bulkDocResults.errors.slice(0, 5).map((err, idx) => (
                              <p key={idx}>• {err}</p>
                            ))}
                            {bulkDocResults.errors.length > 5 && <p>... and {bulkDocResults.errors.length - 5} more</p>}
                          </div>
                        </div>
                      )}
                      
                      {bulkDocResults.updated_riders?.length > 0 && (
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <h4 className="font-semibold text-blue-800 mb-2">Updated Riders:</h4>
                          <div className="max-h-32 overflow-y-auto text-sm">
                            {bulkDocResults.updated_riders.map((rider, idx) => (
                              <div key={idx} className="flex items-center gap-2 py-1">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                <span>{rider.name}</span>
                                <span className="text-gray-500">({rider.emirates_id})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex gap-2 justify-end pt-2">
                    <Button variant="outline" onClick={resetBulkDocDialog}>
                      {bulkDocResults ? 'Close' : 'Cancel'}
                    </Button>
                    {!bulkDocResults && (
                      <Button 
                        onClick={handleBulkDocUpload}
                        disabled={!bulkDocFile || bulkDocUploading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {bulkDocUploading ? (
                          <>
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Documents
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
      </div>

      {/* Lessee-type Summary Batches — External vs In-house (with platform breakdown) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Card className="border-2 border-slate-200 shadow-md bg-gradient-to-br from-slate-50 to-slate-100">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-600">External Lessee Riders</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="external-lessee-count">{lesseeSummary.external}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">nos.</p>
              </div>
              <div className="p-2 bg-slate-200 rounded-full">
                <Users className="h-5 w-5 text-slate-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-indigo-200 shadow-md bg-gradient-to-br from-indigo-50 to-indigo-100">
          <CardContent className="p-3">
            <div
              className="flex items-center justify-between cursor-pointer select-none"
              onClick={() => setShowInhousePlatforms(v => !v)}
              data-testid="inhouse-lessee-toggle"
            >
              <div>
                <p className="text-xs font-medium text-indigo-600 flex items-center gap-1">
                  In-house Lessee Riders
                  <span className="text-[10px] font-normal text-indigo-500">({showInhousePlatforms ? 'hide' : 'show'} platforms)</span>
                </p>
                <p className="text-2xl font-bold text-indigo-800" data-testid="inhouse-lessee-count">{lesseeSummary.inhouse}</p>
                <p className="text-[10px] text-indigo-500 mt-0.5">nos.</p>
              </div>
              <div className="p-2 bg-indigo-200 rounded-full">
                <Users className="h-5 w-5 text-indigo-700" />
              </div>
            </div>
            {showInhousePlatforms && (
              <div className="mt-3 pt-3 border-t border-indigo-200 space-y-1.5">
                {['Talabat','Noon Food','Noon Minutes','Careem','Keeta'].map(p => (
                  <div key={p} className="flex items-center justify-between text-xs">
                    <span className="text-indigo-700">In-house lessee {p}</span>
                    <span className="font-semibold text-indigo-900" data-testid={`inhouse-${p.toLowerCase().replace(/\s+/g,'-')}-count`}>
                      {lesseeSummary.inhouse_by_platform?.[p] || 0} nos.
                    </span>
                  </div>
                ))}
                {lesseeSummary.inhouse_other > 0 && (
                  <div className="flex items-center justify-between text-xs pt-1 border-t border-indigo-100 mt-1">
                    <span className="text-indigo-600 italic">In-house lessee (other / unassigned)</span>
                    <span className="font-semibold text-indigo-800">{lesseeSummary.inhouse_other} nos.</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rider Summary Cards - Interactive */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('all'); setCurrentPage(1); fetchData(1, activeSearch, 'all'); }}
          data-testid="summary-total-riders"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600">Total Riders</p>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
              </div>
              <div className="p-2 bg-blue-200 rounded-full">
                <Users className="h-5 w-5 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-green-50 to-green-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-200' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('active'); setCurrentPage(1); fetchData(1, activeSearch, 'active'); }}
          data-testid="summary-active-riders"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600">Active Riders</p>
                <p className="text-2xl font-bold text-green-800">{stats.active}</p>
              </div>
              <div className="p-2 bg-green-200 rounded-full">
                <UserCheck className="h-5 w-5 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-gray-50 to-gray-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'inactive' ? 'border-gray-500 ring-2 ring-gray-300' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('inactive'); setCurrentPage(1); fetchData(1, activeSearch, 'inactive'); }}
          data-testid="summary-inactive-riders"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600">Inactive Riders</p>
                <p className="text-2xl font-bold text-gray-800">{stats.inactive}</p>
              </div>
              <div className="p-2 bg-gray-200 rounded-full">
                <UserX className="h-5 w-5 text-gray-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 shadow-md bg-gradient-to-br from-amber-50 to-orange-100 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${statusFilter === 'pending' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-transparent'}`}
          onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}
          data-testid="summary-pending-riders"
        >
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-600">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-800">{stats.pending}</p>
              </div>
              <div className="p-2 bg-amber-200 rounded-full">
                <Clock className="h-5 w-5 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Rider List ({filteredRiders.length}{totalCount > filteredRiders.length ? ` of ${totalCount}` : ''})</CardTitle>
            {statusFilter !== 'all' && (
              <Badge className={`${
                statusFilter === 'active' ? 'bg-green-100 text-green-800' : 
                statusFilter === 'pending' ? 'bg-amber-100 text-amber-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                Showing: {statusFilter === 'pending' ? 'pending approval' : statusFilter} riders
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 space-y-4">
            {/* Search Bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                {isSearching ? (
                  <Loader className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4 animate-spin" />
                ) : (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                )}
                <Input
                  type="text"
                  placeholder={t('common.search') + ' (searches all records)'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  className="pl-10 pr-10"
                  data-testid="riders-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="riders-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button 
                onClick={handleSearch}
                disabled={isSearching}
                className="bg-lime-600 hover:bg-lime-700 text-white px-4"
                data-testid="riders-search-button"
              >
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Select value={advancedFilters.emiratesId} onValueChange={(value) => setAdvancedFilters({...advancedFilters, emiratesId: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Emirates ID" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Emirates IDs</SelectItem>
                  {[...new Set(riders.map(r => r.emirates_id).filter(Boolean))].map(eid => (
                    <SelectItem key={eid} value={eid}>{eid}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={advancedFilters.platform} onValueChange={(value) => setAdvancedFilters({...advancedFilters, platform: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {availablePlatforms.map(platform => (
                    <SelectItem key={platform} value={platform}>{platform}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchQuery('');
                  setActiveSearch('');
                  setAdvancedFilters({
                    nationality: 'all',
                    emiratesId: 'all',
                    bikeNo: 'all',
                    platform: 'all'
                  });
                }}
                className="col-span-2 md:col-span-2"
              >
                Clear All Filters
              </Button>
            </div>
          </div>
          
          {/* Mobile/Tablet Card View */}
          {(isMobile || isTablet) ? (
            <div className="space-y-3">
              {filteredRiders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Users className="w-12 h-12 mb-3 text-slate-300" />
                  <p className="text-sm">No riders found</p>
                </div>
              ) : (
                filteredRiders.map((rider) => {
                  const assignedMotorcycle = motorcycles.find(m => m.id === rider.assigned_bike_id);
                  return (
                    <Card 
                      key={rider.id} 
                      className="overflow-hidden active:scale-[0.99] transition-transform cursor-pointer"
                      onClick={() => handleViewDetails(rider)}
                      data-testid={`rider-card-${rider.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Header - ID & Name */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge className="bg-blue-100 text-blue-800 font-mono text-xs">
                                {rider.rider_unique_id || 'N/A'}
                              </Badge>
                              <span className="font-bold text-slate-800 text-lg truncate">
                                {rider.name}
                              </span>
                            </div>

                            {/* Status Badges */}
                            <div className="flex flex-wrap gap-1.5 mb-3">
                              <Badge className={`text-xs ${rider.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                {rider.status}
                              </Badge>
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                {rider.platform === 'Other' && rider.custom_platform ? rider.custom_platform : rider.platform}
                              </Badge>
                              <Badge className={`text-xs ${rider.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}`}>
                                {rider.designation === 'Cyclist' ? 'Cyclist' : 'Motor'}
                              </Badge>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div>
                                <span className="text-slate-500 text-xs">Emirates ID:</span>
                                <p className="text-slate-700 text-xs font-mono truncate">{rider.emirates_id || '-'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Mobile:</span>
                                <p className="text-slate-700">{rider.mobile_uae || '-'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Company:</span>
                                <p className="text-slate-700 truncate">{rider.lessee_name || '-'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500 text-xs">Motorcycle:</span>
                                {rider.assigned_bike_plate ? (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">{String(rider.assigned_bike_plate)}</Badge>
                                ) : assignedMotorcycle && assignedMotorcycle.plate_number ? (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">{String(assignedMotorcycle.plate_number)}</Badge>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(rider); }}>
                                  <User className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewDocuments(rider)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Documents
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewHistory(rider)}>
                                  <History className="mr-2 h-4 w-4" />
                                  View History
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEdit(rider)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Rider
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDelete(rider.id)} className="text-red-600">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Rider
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="rounded-lg border border-slate-200 shadow-sm">
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap px-2 py-2">ID</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Name</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Emirates ID</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Mobile</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Platform</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Platform ID</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Lessee</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Motorcycle</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Type</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2">Status</TableHead>
                  <TableHead className="whitespace-nowrap px-2 py-2 text-center w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRiders.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center text-slate-500 py-8">No riders found</TableCell></TableRow>
                ) : (
                  filteredRiders.map((rider) => {
                    const assignedMotorcycle = motorcycles.find(m => m.id === rider.assigned_bike_id);
                    return (
                    <TableRow 
                      key={rider.id} 
                      data-testid={`rider-row-${rider.id}`}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleViewDetails(rider)}
                    >
                      <TableCell className="text-xs font-mono bg-blue-50 text-blue-800 font-semibold px-2 py-1.5 whitespace-nowrap">
                        {rider.rider_unique_id || 'N/A'}
                      </TableCell>
                      <TableCell className="font-medium px-2 py-1.5 max-w-[120px] truncate" title={rider.name}>{rider.name}</TableCell>
                      <TableCell className="text-xs px-2 py-1.5 whitespace-nowrap">{rider.emirates_id}</TableCell>
                      <TableCell className="text-xs px-2 py-1.5 whitespace-nowrap">{rider.mobile_uae || '-'}</TableCell>
                      <TableCell className="px-2 py-1.5"><Badge className="bg-purple-100 text-purple-800 text-xs px-1.5 py-0.5">{rider.platform === 'Other' && rider.custom_platform ? rider.custom_platform : rider.platform}</Badge></TableCell>
                      <TableCell className="text-xs font-mono text-orange-700 px-2 py-1.5 whitespace-nowrap">{rider.platform_id || <span className="text-slate-400">-</span>}</TableCell>
                      <TableCell className="text-xs px-2 py-1.5 max-w-[100px] truncate" title={rider.lessee_name}>{rider.lessee_name || 'N/A'}</TableCell>
                      <TableCell className="px-2 py-1.5">
                        {rider.assigned_bike_plate ? (
                          <Badge className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">{String(rider.assigned_bike_plate)}</Badge>
                        ) : assignedMotorcycle && assignedMotorcycle.plate_number ? (
                          <Badge className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">{String(assignedMotorcycle.plate_number)}</Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="px-2 py-1.5">
                        <Badge className={`text-xs px-1.5 py-0.5 ${rider.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}`}>
                          {rider.designation === 'Cyclist' ? 'Cyclist' : 'Motor'}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-2 py-1.5"><Badge className={`text-xs px-1.5 py-0.5 ${rider.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{rider.status}</Badge></TableCell>
                      <TableCell className="px-2 py-1.5">
                        <div onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                data-testid={`actions-rider-${rider.id}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(rider); }} className="cursor-pointer">
                                <User className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewDocuments(rider)} className="cursor-pointer">
                                <Eye className="mr-2 h-4 w-4" />
                                View Documents
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewHistory(rider)} className="cursor-pointer" data-testid={`history-rider-${rider.id}`}>
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(rider)} className="cursor-pointer" data-testid={`edit-rider-${rider.id}`}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Rider
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(rider.id)} className="cursor-pointer text-red-600 focus:text-red-600" data-testid={`delete-rider-${rider.id}`}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Rider
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
              totalCount={totalCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
          )}
        </CardContent>
      </Card>

      {/* Rider Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Rider Details - {selectedRider?.name}
            </DialogTitle>
            <DialogDescription>Complete information, vehicle history, fines, and maintenance records</DialogDescription>
          </DialogHeader>
          {selectedRider && (
            <Tabs value={riderDetailTab} onValueChange={setRiderDetailTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-4">
                <TabsTrigger value="info" className="gap-1">
                  <User className="w-4 h-4" />
                  Info
                </TabsTrigger>
                <TabsTrigger value="vehicle" className="gap-1">
                  <Bike className="w-4 h-4" />
                  Vehicle
                </TabsTrigger>
                <TabsTrigger value="fines" className="gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Fines
                </TabsTrigger>
                <TabsTrigger value="maintenance" className="gap-1">
                  <Wrench className="w-4 h-4" />
                  Maintenance
                </TabsTrigger>
              </TabsList>
              
              {/* Info Tab */}
              <TabsContent value="info" className="space-y-4">
                {/* Personal Information */}
                <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Personal Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Full Name</Label>
                      <p className="font-medium">{selectedRider.name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Date of Birth</Label>
                      <p className="font-medium">{selectedRider.date_of_birth ? formatDateDisplay(selectedRider.date_of_birth) : 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Nationality</Label>
                      <p className="font-medium">{selectedRider.nationality || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Status</Label>
                      <Badge className={selectedRider.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {selectedRider.status}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Email</Label>
                      <p className="font-medium">{selectedRider.email || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">UAE Mobile</Label>
                      <p className="font-medium">{selectedRider.mobile_uae || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Home Country Mobile</Label>
                      <p className="font-medium">{selectedRider.mobile_home_country || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Emergency Contact</Label>
                      <p className="font-medium">{selectedRider.emergency_contact_name || 'N/A'}</p>
                      <p className="text-sm text-slate-600">{selectedRider.emergency_contact_number || ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Documentation */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Documentation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Emirates ID</Label>
                      <p className="font-medium">{selectedRider.emirates_id || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Expiry: {selectedRider.emirates_id_expiry ? formatDateDisplay(selectedRider.emirates_id_expiry) : 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Passport Number</Label>
                      <p className="font-medium">{selectedRider.passport_number || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Expiry: {selectedRider.passport_expiry ? formatDateDisplay(selectedRider.passport_expiry) : 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Visa UID</Label>
                      <p className="font-medium">{selectedRider.visa_uid || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Expiry: {selectedRider.visa_expiry ? formatDateDisplay(selectedRider.visa_expiry) : 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">License Number</Label>
                      <p className="font-medium">{selectedRider.license_number || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Expiry: {selectedRider.license_expiry ? formatDateDisplay(selectedRider.license_expiry) : 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Employment */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Employment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Lessee Company</Label>
                      <p className="font-medium">{selectedRider.lessee_name || 'Not assigned'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Current Platform</Label>
                      <p className="font-medium">{selectedRider.platform || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Platform ID</Label>
                      <p className="font-mono">{selectedRider.platform_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Designation</Label>
                      <Badge className={selectedRider.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}>
                        {selectedRider.designation || 'Motorcyclist'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Platform History */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Platform History</CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setEditPlatformHistoryIndex(null);
                      setAddPlatformHistoryDialogOpen(true);
                      setNewPlatformEntry({ 
                        joining_date: new Date().toISOString().split('T')[0], 
                        last_date: '', 
                        platform: '', 
                        platform_id: '', 
                        custom_platform: '',
                        new_mobile_number: '',
                        sim_type: '',
                        sim_owner: ''
                      });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Entry
                  </Button>
                </CardHeader>
                <CardContent>
                  {platformHistoryLoading ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                  ) : platformHistory.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No platform history available</p>
                  ) : (
                    <>
                      {(() => {
                        const hasPrimary = platformHistory.some(e => e.is_primary);
                        if (platformHistory.length > 1 && !hasPrimary) {
                          return (
                            <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2" data-testid="primary-platform-warning">
                              <AlertTriangle className="w-4 h-4 shrink-0" />
                              <span>This rider has multiple platforms. <strong>Mark one as Primary</strong> using the star icon to control where deductions are applied during payroll.</span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Joining Date</TableHead>
                          <TableHead>Last Date</TableHead>
                          <TableHead>Platform</TableHead>
                          <TableHead>Platform ID</TableHead>
                          <TableHead className="w-32">Primary</TableHead>
                          <TableHead className="w-16">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...platformHistory].reverse().map((entry, idx) => {
                          const originalIndex = platformHistory.length - 1 - idx;
                          const isPrimary = !!entry.is_primary;
                          const isOnlyOne = platformHistory.length === 1;
                          return (
                            <TableRow key={idx} className={isPrimary ? 'bg-amber-50/40' : ''}>
                              <TableCell>{formatDateDMY(entry.joining_date)}</TableCell>
                              <TableCell>
                                {entry.last_date ? (
                                  formatDateDMY(entry.last_date)
                                ) : (
                                  <Badge className="bg-green-100 text-green-800">Current</Badge>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {entry.custom_platform || entry.platform || '-'}
                              </TableCell>
                              <TableCell className="font-mono">{entry.platform_id || '-'}</TableCell>
                              <TableCell>
                                {isPrimary ? (
                                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 gap-1" data-testid="primary-platform-badge">
                                    <Star className="w-3 h-3 fill-amber-600 text-amber-600" />
                                    Primary
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs text-slate-600 hover:text-amber-700"
                                    onClick={() => handleSetPrimaryPlatform(originalIndex)}
                                    disabled={isOnlyOne}
                                    title={isOnlyOne ? 'Single platform is automatically primary' : 'Mark as Primary Platform'}
                                    data-testid={`set-primary-${originalIndex}`}
                                  >
                                    <Star className="w-3.5 h-3.5 mr-1" /> Set Primary
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleEditPlatformHistory(entry, originalIndex)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Assigned Vehicle */}
              {selectedRider.assigned_bike_plate && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Assigned Vehicle</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600">Plate Number</Label>
                        <p className="font-mono font-semibold text-lg">{selectedRider.assigned_bike_plate}</p>
                      </div>
                      <div>
                        <Label className="text-slate-600">Bike ID</Label>
                        <p className="font-mono text-sm text-slate-500">{selectedRider.assigned_bike_id || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              </TabsContent>
              
              {/* Vehicle History Tab */}
              <TabsContent value="vehicle" className="space-y-4">
                {/* Current Assignment */}
                <Card className="border-green-200 bg-green-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bike className="w-5 h-5 text-green-600" />
                      Current Assignment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Check rider record first, then fall back to most recent active handover from history
                      const currentPlate = selectedRider.assigned_bike_plate;
                      const currentBikeId = selectedRider.assigned_bike_id;
                      
                      // If rider doesn't have assigned bike, check vehicle history for active handover
                      const activeHandover = !currentPlate && vehicleHistory.find(
                        h => h.type === 'handover' && h.status === 'assigned'
                      );
                      
                      const displayPlate = currentPlate || activeHandover?.bike_plate;
                      const displayBikeId = currentBikeId || activeHandover?.bike_id;
                      const isFromHistory = !currentPlate && activeHandover;
                      
                      if (displayPlate) {
                        return (
                          <div>
                            <div className="flex items-center gap-4">
                              <div className="bg-white px-4 py-2 rounded-lg border border-green-200">
                                <p className="text-xs text-slate-500">Plate Number</p>
                                <p className="font-mono font-bold text-xl text-green-700">{displayPlate}</p>
                              </div>
                              <div className="bg-white px-4 py-2 rounded-lg border border-green-200">
                                <p className="text-xs text-slate-500">Bike ID</p>
                                <p className="font-mono text-sm">{displayBikeId || '-'}</p>
                              </div>
                            </div>
                            {isFromHistory && (
                              <p className="text-xs text-amber-600 mt-2">
                                ⚠️ Assignment detected from handover history. Please run "Sync Bike Assignments" to update rider records.
                              </p>
                            )}
                          </div>
                        );
                      } else {
                        return <p className="text-slate-500 italic">No vehicle currently assigned</p>;
                      }
                    })()}
                  </CardContent>
                </Card>
                
                {/* Vehicle Assignment History */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Assignment History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {vehicleHistoryLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : vehicleHistory.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No vehicle assignment history found</p>
                    ) : (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto">
                        {vehicleHistory.map((item, idx) => (
                          <div key={item.id || idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              item.type === 'handover' ? 'bg-green-100 text-green-600' :
                              item.type === 'takeover' ? 'bg-red-100 text-red-600' :
                              item.type === 'accident' ? 'bg-amber-100 text-amber-600' :
                              item.type === 'replacement' ? 'bg-blue-100 text-blue-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {item.type === 'handover' ? <ChevronRight className="w-5 h-5" /> :
                               item.type === 'takeover' ? <X className="w-5 h-5" /> :
                               item.type === 'accident' ? <AlertTriangle className="w-5 h-5" /> :
                               <Bike className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-semibold">{item.bike_plate || '-'}</span>
                                <Badge variant="outline" className="capitalize text-xs">
                                  {item.type}
                                </Badge>
                                {item.status === 'assigned' && <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>}
                              </div>
                              <p className="text-xs text-slate-500">
                                {item.date ? formatDateDisplay(item.date) : '-'}
                                {item.lessee_name && ` • ${item.lessee_name}`}
                                {item.reason && ` • ${item.reason}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Fines History Tab */}
              <TabsContent value="fines" className="space-y-4">
                {/* Fines Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4">
                      <p className="text-xs text-amber-600">Total Fines</p>
                      <p className="text-2xl font-bold text-amber-700">AED {finesHistory.total_amount?.toLocaleString() || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-4">
                      <p className="text-xs text-green-600">Paid</p>
                      <p className="text-2xl font-bold text-green-700">AED {finesHistory.paid_amount?.toLocaleString() || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="pt-4">
                      <p className="text-xs text-red-600">Unpaid</p>
                      <p className="text-2xl font-bold text-red-700">AED {finesHistory.unpaid_amount?.toLocaleString() || 0}</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Assigned Vehicles Info */}
                {finesHistory.assigned_vehicles?.length > 0 && (
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-slate-500 mb-2">Fines shown for vehicles:</p>
                      <div className="flex flex-wrap gap-2">
                        {finesHistory.assigned_vehicles.map((plate, idx) => (
                          <Badge key={idx} variant="outline" className="font-mono">{plate}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Fines List */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Fine Records ({finesHistory.fines?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {finesHistoryLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : finesHistory.fines?.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No fines found for assigned vehicles</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Date</TableHead>
                            <TableHead>Bike Plate</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {finesHistory.fines?.slice(0, 20).map((fine, idx) => (
                            <TableRow key={fine.id || idx}>
                              <TableCell className="text-xs">
                                {fine.fine_date || fine.date_of_fine ? formatDateDisplay(fine.fine_date || fine.date_of_fine) : '-'}
                              </TableCell>
                              <TableCell className="font-mono text-xs">{fine.bike_plate_number || '-'}</TableCell>
                              <TableCell className="text-xs">{fine.fine_type || fine.type || '-'}</TableCell>
                              <TableCell className="text-xs font-medium">
                                AED {((fine.amount || 0) + (fine.admin_fees || 0)).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  fine.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                  'bg-red-100 text-red-700'
                                }>
                                  {fine.payment_status || 'unpaid'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {finesHistory.fines?.length > 20 && (
                      <p className="text-xs text-slate-500 text-center mt-2">Showing 20 of {finesHistory.fines.length} fines</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Maintenance History Tab */}
              <TabsContent value="maintenance" className="space-y-4">
                {/* Maintenance Summary */}
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600">Total Maintenance Cost</p>
                        <p className="text-2xl font-bold text-blue-700">AED {maintenanceHistory.total_cost?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-blue-600">Records</p>
                        <p className="text-2xl font-bold text-blue-700">{maintenanceHistory.maintenance?.length || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Assigned Vehicles Info */}
                {maintenanceHistory.assigned_vehicles?.length > 0 && (
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-slate-500 mb-2">Maintenance shown for vehicles:</p>
                      <div className="flex flex-wrap gap-2">
                        {maintenanceHistory.assigned_vehicles.map((plate, idx) => (
                          <Badge key={idx} variant="outline" className="font-mono">{plate}</Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Maintenance List */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wrench className="w-5 h-5" />
                      Maintenance Records ({maintenanceHistory.maintenance?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {maintenanceHistoryLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : maintenanceHistory.maintenance?.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No maintenance records found for assigned vehicles</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Date</TableHead>
                            <TableHead>Bike Plate</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Cost</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {maintenanceHistory.maintenance?.slice(0, 20).map((m, idx) => (
                            <TableRow key={m.id || idx}>
                              <TableCell className="text-xs">
                                {m.date ? formatDateDisplay(m.date) : '-'}
                              </TableCell>
                              <TableCell className="font-mono text-xs">{m.bike_plate_number || '-'}</TableCell>
                              <TableCell className="text-xs capitalize">{m.maintenance_type || m.type || '-'}</TableCell>
                              <TableCell className="text-xs max-w-[150px] truncate" title={m.description}>
                                {m.description || '-'}
                              </TableCell>
                              <TableCell className="text-xs font-medium">
                                AED {(m.total_cost || m.cost || 0).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Badge className={
                                  m.status === 'completed' ? 'bg-green-100 text-green-700' :
                                  m.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-700'
                                }>
                                  {m.status || 'pending'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {maintenanceHistory.maintenance?.length > 20 && (
                      <p className="text-xs text-slate-500 text-center mt-2">Showing 20 of {maintenanceHistory.maintenance.length} records</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Platform History Dialog */}
      <Dialog open={addPlatformHistoryDialogOpen} onOpenChange={(open) => {
        setAddPlatformHistoryDialogOpen(open);
        if (!open) setEditPlatformHistoryIndex(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editPlatformHistoryIndex !== null ? 'Edit' : 'Add'} Platform History Entry</DialogTitle>
            <DialogDescription>
              {editPlatformHistoryIndex !== null ? 'Edit' : 'Add a new'} platform history entry for {selectedRider?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Joining Date *</Label>
                <Input 
                  type="date" 
                  value={newPlatformEntry.joining_date}
                  onChange={(e) => setNewPlatformEntry({...newPlatformEntry, joining_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Last Date (leave empty if current)</Label>
                <Input 
                  type="date" 
                  value={newPlatformEntry.last_date}
                  onChange={(e) => setNewPlatformEntry({...newPlatformEntry, last_date: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Platform *</Label>
              <Select 
                value={newPlatformEntry.platform} 
                onValueChange={(value) => setNewPlatformEntry({...newPlatformEntry, platform: value, custom_platform: value === 'Other' ? newPlatformEntry.custom_platform : ''})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Talabat">Talabat</SelectItem>
                  <SelectItem value="Noon">Noon</SelectItem>
                  <SelectItem value="Careem">Careem</SelectItem>
                  <SelectItem value="Deliveroo">Deliveroo</SelectItem>
                  <SelectItem value="Keeta">Keeta</SelectItem>
                  <SelectItem value="InstaShop">InstaShop</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newPlatformEntry.platform === 'Other' && (
              <div className="space-y-2">
                <Label>Custom Platform Name</Label>
                <Input 
                  value={newPlatformEntry.custom_platform}
                  onChange={(e) => setNewPlatformEntry({...newPlatformEntry, custom_platform: e.target.value})}
                  placeholder="Enter platform name"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Platform ID</Label>
              <Input 
                value={newPlatformEntry.platform_id}
                onChange={(e) => setNewPlatformEntry({...newPlatformEntry, platform_id: e.target.value})}
                placeholder="Enter platform ID"
              />
            </div>

            {/* New Mobile No. + SIM Type + Owner — captured at platform change so
                the SIM inventory / rider's latest phone stay in sync. */}
            <div className="pt-3 mt-1 border-t border-slate-200">
              <div className="text-[13px] font-semibold text-slate-800 mb-2">
                New Mobile no. (optional)
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-2">
                  <Label>Enter new Mobile no.</Label>
                  <Input
                    type="tel"
                    value={newPlatformEntry.new_mobile_number}
                    onChange={(e) => setNewPlatformEntry({ ...newPlatformEntry, new_mobile_number: e.target.value })}
                    placeholder="+971 5X XXX XXXX"
                    data-testid="ph-new-mobile"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>SIM Type</Label>
                    <Select
                      value={newPlatformEntry.sim_type}
                      onValueChange={(v) => setNewPlatformEntry({ ...newPlatformEntry, sim_type: v, sim_owner: '' })}
                    >
                      <SelectTrigger data-testid="ph-sim-type"><SelectValue placeholder="Select SIM type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Company SIM card</SelectItem>
                        <SelectItem value="platform">Platform SIM card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Owner</Label>
                    <Select
                      value={newPlatformEntry.sim_owner}
                      onValueChange={(v) => setNewPlatformEntry({ ...newPlatformEntry, sim_owner: v })}
                      disabled={!newPlatformEntry.sim_type}
                    >
                      <SelectTrigger data-testid="ph-sim-owner">
                        <SelectValue placeholder={newPlatformEntry.sim_type ? 'Select owner' : 'Choose SIM type first'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(newPlatformEntry.sim_type === 'company'
                          ? ['GEDS', 'EMS']
                          : ['Noon', 'Talabat', 'Keeta', 'Careem', 'Noon Minutes', 'Deliveroo']
                        ).map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-2 leading-snug">
                On save this becomes the rider&apos;s latest mobile number. If it does not match the SIM Card Inventory for this rider, a reminder will appear — update the SIM Card Inventory page to complete the sync.
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddPlatformHistoryDialogOpen(false);
              setEditPlatformHistoryIndex(null);
            }}>Cancel</Button>
            <Button onClick={handleAddPlatformHistory}>
              {editPlatformHistoryIndex !== null ? 'Save Changes' : 'Add Entry'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassignment Confirmation Dialog */}
      <Dialog open={reassignmentDialog} onOpenChange={setReassignmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Motorcycle Already Assigned</DialogTitle>
            <DialogDescription>
              This motorcycle is currently assigned to another rider.
            </DialogDescription>
          </DialogHeader>
          
          {reassignmentData && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-medium text-sm">Current Assignment:</p>
                <div className="mt-2 space-y-1 text-sm">
                  <p><span className="font-medium">Motorcycle:</span> {reassignmentData.bike?.plate_number}</p>
                  <p><span className="font-medium">Current Rider:</span> {reassignmentData.current_rider?.name}</p>
                  <p><span className="font-medium">Mobile:</span> {reassignmentData.current_rider?.mobile}</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-700">
                If you proceed, this motorcycle will be unassigned from <strong>{reassignmentData.current_rider?.name}</strong> and assigned to the current rider.
              </p>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setReassignmentDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleReassignmentConfirm} className="bg-red-600 hover:bg-red-700">
                  Proceed with Reassignment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Warning/Overrule Dialog */}
      <Dialog open={warningDialog} onOpenChange={setWarningDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-orange-600">⚠️ Document Expiry Warning</DialogTitle>
            <DialogDescription>
              The selected motorcycle has expired documents. Review the warnings below.
            </DialogDescription>
          </DialogHeader>
          
          {warningData && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-medium text-sm mb-2">⛔ Expired Documents:</p>
                <div className="space-y-2">
                  {warningData.warnings.map((warning, index) => (
                    <div key={index} className="flex items-start gap-2 text-sm">
                      <span className="text-red-600">•</span>
                      <div>
                        <p className="font-medium">{warning.type === 'expired_insurance' ? 'Insurance' : 'Registration'}</p>
                        <p className="text-gray-600">{warning.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-medium text-sm">Motorcycle Details:</p>
                <div className="mt-2 space-y-1 text-sm">
                  <p><span className="font-medium">Plate:</span> {warningData.bike?.plate_number}</p>
                  <p><span className="font-medium">Model:</span> {warningData.bike?.model_name}</p>
                </div>
              </div>
              
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-semibold text-sm text-yellow-800">⏰ Important Notice:</p>
                <p className="text-sm text-gray-700 mt-1">
                  If you proceed with this assignment, you must correct these expired documents within <strong>3 working days</strong>. 
                  This overrule will be logged and tracked in the critical alerts banner.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Overrule Notes (Optional)</Label>
                <Input
                  placeholder="Reason for overruling warning..."
                  value={overruleNotes}
                  onChange={(e) => setOverruleNotes(e.target.value)}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setWarningDialog(false);
                  setOverruleNotes('');
                }}>
                  Cancel Assignment
                </Button>
                <Button onClick={handleOverruleWarning} className="bg-orange-600 hover:bg-orange-700">
                  Accept & Proceed
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rider History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Rider History
            </DialogTitle>
            <DialogDescription>
              {historyRider && (
                <span>
                  Complete change history for <strong>{truncateName(historyRider.name)}</strong> ({historyRider.rider_unique_id || 'N/A'})
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-purple-600" />
              <span className="ml-2">Loading history...</span>
            </div>
          ) : riderHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No history records found</p>
              <p className="text-sm">Changes to platform, status, or bike assignments will be recorded here</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-500 mb-2">
                Total changes: {riderHistory.length}
              </div>
              
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-t-lg border border-slate-200 text-xs font-semibold text-slate-600">
                <div className="col-span-2">Date & Time</div>
                <div className="col-span-2">Change Type</div>
                <div className="col-span-2">Field</div>
                <div className="col-span-2">Old Value</div>
                <div className="col-span-2">New Value</div>
                <div className="col-span-2">Changed By</div>
              </div>
              
              {/* Table Body */}
              <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100">
                {riderHistory.map((entry, index) => {
                  // Truncate values for display (names and company names)
                  const shouldTruncate = ['employer', 'lessee', 'name', 'rider'].some(
                    keyword => entry.field_changed?.toLowerCase().includes(keyword)
                  );
                  const displayOldValue = shouldTruncate ? truncateName(entry.old_value) : (entry.old_value || 'Not Set');
                  const displayNewValue = shouldTruncate ? truncateName(entry.new_value) : (entry.new_value || 'Not Set');
                  
                  return (
                    <div 
                      key={entry.id || index}
                      className={`grid grid-cols-12 gap-2 px-3 py-3 text-xs items-center ${
                        index % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                      } hover:bg-blue-50 transition-colors cursor-pointer group`}
                      title={`Old: ${entry.old_value || 'Not Set'} → New: ${entry.new_value || 'Not Set'}`}
                    >
                      {/* Date & Time */}
                      <div className="col-span-2 text-slate-600">
                        <div className="font-medium">{formatDateDisplay(entry.changed_at)}</div>
                        <div className="text-slate-400">{new Date(entry.changed_at).toLocaleTimeString()}</div>
                      </div>
                      
                      {/* Change Type */}
                      <div className="col-span-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${
                          entry.change_type === 'platform_change' ? 'bg-blue-100 text-blue-700' :
                          entry.change_type === 'status_change' ? 'bg-yellow-100 text-yellow-700' :
                          entry.change_type === 'bike_assignment' ? 'bg-green-100 text-green-700' :
                          entry.change_type === 'employer_change' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {entry.change_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                      </div>
                      
                      {/* Field */}
                      <div className="col-span-2 font-medium text-slate-700">
                        {entry.field_changed}
                      </div>
                      
                      {/* Old Value */}
                      <div className="col-span-2">
                        <span className="px-2 py-1 bg-red-50 text-red-600 rounded text-xs line-through" title={entry.old_value || 'Not Set'}>
                          {displayOldValue}
                        </span>
                      </div>
                      
                      {/* New Value */}
                      <div className="col-span-2">
                        <span className="px-2 py-1 bg-green-50 text-green-600 rounded text-xs font-medium" title={entry.new_value || 'Not Set'}>
                          {displayNewValue}
                        </span>
                      </div>
                      
                      {/* Changed By */}
                      <div className="col-span-2 text-slate-600 font-medium">
                        {truncateName(entry.changed_by)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="flex justify-end mt-4">
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog open={documentViewerOpen} onOpenChange={setDocumentViewerOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Rider Documents
            </DialogTitle>
            <DialogDescription>
              {documentViewerRider && (
                <span>
                  Documents for <strong>{documentViewerRider.name}</strong> ({documentViewerRider.rider_unique_id || 'N/A'})
                  {riderDocuments && ` — ${riderDocuments.total_documents} document${riderDocuments.total_documents !== 1 ? 's' : ''} available`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDocs ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 animate-spin text-blue-500 mr-2" />
              <span className="text-slate-500">Loading documents...</span>
            </div>
          ) : riderDocuments && riderDocuments.total_documents > 0 ? (
            <div className="space-y-5">
              {/* Download All Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadRiderDocuments(documentViewerRider)}
                  className="gap-2"
                  data-testid="download-all-docs-btn"
                >
                  <Download className="w-4 h-4" />
                  Download All (ZIP)
                </Button>
              </div>
              
              {/* Categorized Document Sections */}
              {Object.entries(riderDocuments.categories).map(([catKey, category]) => (
                category.count > 0 && (
                  <div key={catKey} data-testid={`doc-category-${catKey}`}>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      {category.label}
                      <Badge variant="secondary" className="text-xs">{category.count}</Badge>
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {category.documents.map((doc, index) => (
                        <div 
                          key={`${catKey}-${index}`}
                          className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                          data-testid={`doc-card-${doc.field}`}
                        >
                          {/* Document Preview */}
                          <div className="aspect-[4/3] bg-slate-50 relative group">
                            {doc.url.match(/^data:image|\.(?:jpg|jpeg|png|gif|webp)(?:\?|$)/i) ? (
                              <img 
                                src={doc.url} 
                                alt={doc.label}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <div className={`w-full h-full flex flex-col items-center justify-center ${doc.url.match(/^data:image|\.(?:jpg|jpeg|png|gif|webp)(?:\?|$)/i) ? 'hidden' : ''}`}>
                              {doc.url.match(/\.pdf(?:\?|$)/i) || doc.url.startsWith('data:application/pdf') ? (
                                <>
                                  <FileText className="w-10 h-10 text-red-400 mb-1" />
                                  <span className="text-xs text-red-500 font-medium">PDF</span>
                                </>
                              ) : (
                                <>
                                  <FileText className="w-10 h-10 text-slate-300 mb-1" />
                                  <span className="text-xs text-slate-400">Document</span>
                                </>
                              )}
                            </div>
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button size="sm" variant="secondary" onClick={() => window.open(doc.url, '_blank')} className="gap-1">
                                <Eye className="w-3 h-3" /> View
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => handleDownloadSingleDocument(doc.url, `${documentViewerRider.name}_${doc.label.replace(/\s/g, '_')}`)} className="gap-1">
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Document Label */}
                          <div className="px-2 py-1.5 border-t">
                            <p className="text-xs font-medium text-slate-700 truncate">{doc.label}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          ) : riderDocuments && riderDocuments.total_documents === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">No Documents Available</p>
              <p className="text-sm mt-1">This rider has no uploaded documents</p>
            </div>
          ) : documentViewerRider ? (
            /* Fallback to local data if backend fetch failed */
            <>
              {getRiderDocuments(documentViewerRider).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No Documents Available</p>
                  <p className="text-sm mt-1">This rider has no uploaded documents</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => handleDownloadRiderDocuments(documentViewerRider)} className="gap-2">
                      <Download className="w-4 h-4" /> Download All (ZIP)
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {getRiderDocuments(documentViewerRider).map((doc, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-[4/3] bg-slate-100 relative group">
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <FileText className="w-12 h-12 text-slate-400 mb-2" />
                            <span className="text-xs text-slate-500">Document</span>
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <Button size="sm" variant="secondary" onClick={() => window.open(doc.url, '_blank')} className="gap-1"><Eye className="w-4 h-4" /> View</Button>
                            <Button size="sm" variant="secondary" onClick={() => handleDownloadSingleDocument(doc.url, `${documentViewerRider.name}_${doc.type.replace(/\s/g, '_')}`)} className="gap-1"><Download className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <div className="p-3 border-t">
                          <p className="text-sm font-medium text-slate-800 truncate">{doc.type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDocumentViewerOpen(false)} data-testid="close-docs-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Riders;
